import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { inspectionsApi, repairsApi } from '@/services/api';
import MediaQualityToggle from './MediaQualityToggle';
import BulkMediaDownloadButton from './BulkMediaDownloadButton';
import {
  Loader2, User, Activity, Car, FileText, Settings, Wrench, Shield,
  AlertTriangle, CheckCircle, Clock, RefreshCw, Save, Pencil, X,
  ChevronDown, ChevronRight, DollarSign, Gauge, History, Download,
  Star, ThumbsUp, ThumbsDown, AlertCircle, Info, Zap, Eye, Share2,
  ExternalLink, Copy, ClipboardList, CircleDot, RotateCcw, Package,
  Sparkles, Lightbulb, MapPin, CalendarClock, BarChart3, Check, Camera,
  Edit2, EyeOff, Database, Phone
} from 'lucide-react';

// Condition options for dropdowns
import { Section, EditableField } from './helpers';
import RatingBreakdownModal from './RatingBreakdownModal';
import SaveToMasterModal from './SaveToMasterModal';
import RepairItem from './RepairItem';
import AIAnalysisTab from './AIAnalysisTab';
import VehicleTab from './VehicleTab';
import QATab from './QATab';
import OBDTab from './OBDTab';
import RepairsTab from './RepairsTab';
import ReportTab from './ReportTab';
import ServiceHistoryTab from './ServiceHistoryTab';
const CONDITION_OPTIONS = ['EXCELLENT', 'GOOD', 'FAIR', 'POOR', 'NOT_INSPECTED'];
const REPAIR_TYPES = ['MINOR', 'MAJOR', 'CRITICAL'];

// Car type options (must match repairs module)
const CAR_TYPES = ['hatchback', 'sedan', 'suv'];

// Common Indian car brands (must match repairs module)
const CAR_BRANDS = [
  'Maruti Suzuki', 'Hyundai', 'Tata', 'Mahindra', 'Kia', 'Toyota',
  'Honda', 'Ford', 'Volkswagen', 'Skoda', 'Renault', 'Nissan',
  'MG', 'Jeep', 'BMW', 'Mercedes-Benz', 'Audi', 'Land Rover'
];

// Action types for repair items
const ACTION_TYPES = [
  { value: 'repair', label: 'Repair' },
  { value: 'replace', label: 'Replace' }
];

// Statuses where a mechanic may still be filling the inspection in — only these
// deserve a 5s poll. Anything past them (COMPLETED / PUBLISHED / CANCELLED) is
// static, and polling it every 5s from a forgotten open tab is what drained the
// backend's Mongo pool and took the whole CRM + mechanic app down (P0 2026-09-21).
const LIVE_POLL_STATUSES = [
  'NEW_INSPECTION', 'ASSIGNED_TO_MECHANIC', 'MECHANIC_ACCEPTED',
  'INSPECTION_STARTED', 'INSPECTION_IN_PROGRESS', 'RESCHEDULED',
];




// Main Live Progress Modal Component
export default function LiveProgressModal({
  isOpen,
  onClose,
  inspection,
  liveProgressData,
  onRefresh,
  onInspectionUpdated, // NEW: callback to update inspection data
  canEdit = false,
  user
}) {
  // Local state for editing
  const [activeTab, setActiveTab] = useState('ai-analysis');
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true); // Auto-refresh enabled by default
  const [refreshing, setRefreshing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [generatingShareUrl, setGeneratingShareUrl] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState(null); // { questionId, categoryId }
  const [savingAnswer, setSavingAnswer] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null); // For filtering Q&A by category
  
  // OBD Rescan state
  const [requestingRescan, setRequestingRescan] = useState(false);
  
  // DTC Analysis state
  const [analyzingDTC, setAnalyzingDTC] = useState(false);
  const [dtcAnalysis, setDtcAnalysis] = useState(null);
  const [dtcFilterType, setDtcFilterType] = useState('all'); // 'all', 'critical', 'critical_important'
  const [selectedDtcCodes, setSelectedDtcCodes] = useState(new Set());
  const [savingDtcSelection, setSavingDtcSelection] = useState(false);
  const [expandedDtcGroups, setExpandedDtcGroups] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState({ History: true, Pending: true, Active: true });
  // Hierarchy filter output — controls which codes are DISPLAYED in the DTC list.
  // Separate from selectedDtcCodes (which tracks report selection checkboxes).
  const [hierarchyFilteredCodes, setHierarchyFilteredCodes] = useState(null);

  // Reset DTC state when inspection changes to avoid showing stale data
  useEffect(() => {
    setDtcAnalysis(null);
    setSelectedDtcCodes(new Set());
    setHierarchyFilteredCodes(null);
    setDtcFilterType('all');
    setExpandedDtcGroups(new Set());
    setAnalyzingDTC(false);
  }, [inspection?.id]);
  
  // RPP (Recommended Purchase Price) state
  const [fetchingRPP, setFetchingRPP] = useState(false);
  
  // Repairs Module state
  const [repairParts, setRepairParts] = useState([]);
  const [repairRules, setRepairRules] = useState([]);
  const [partCategories, setPartCategories] = useState([]);
  const [inspectionQuestions, setInspectionQuestions] = useState([]);
  const [loadingRepairs, setLoadingRepairs] = useState(false);
  const [calculatedRepairs, setCalculatedRepairs] = useState([]);
  
  // Repair Report Selection state (similar to DTC codes)
  const [selectedRepairs, setSelectedRepairs] = useState(new Set());
  const [repairCostOverrides, setRepairCostOverrides] = useState({}); // { repairKey: { price, labor, cost } }
  const [editingRepairCost, setEditingRepairCost] = useState(null); // repairKey being edited
  const [savingRepairSelection, setSavingRepairSelection] = useState(false);
  const [updatingMasterCost, setUpdatingMasterCost] = useState(null); // part_id being updated
  
  // Report Publish state
  const [publishHistory, setPublishHistory] = useState([]);
  const [loadingPublishHistory, setLoadingPublishHistory] = useState(false);
  const [publishingReport, setPublishingReport] = useState(false);
  const [publishNotes, setPublishNotes] = useState('');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [portalAudit, setPortalAudit] = useState({ events: [], active_otp: null });
  
  // KM Extraction from Dashboard Photo state
  const [extractingKM, setExtractingKM] = useState(false);
  const [kmExtractionResult, setKmExtractionResult] = useState(null);
  const [kmExtractionProcessed, setKmExtractionProcessed] = useState(false);
  const [editingKmReading, setEditingKmReading] = useState(false);
  const [manualKmValue, setManualKmValue] = useState('');
  const [showDashboardPhotoModal, setShowDashboardPhotoModal] = useState(false);
  const [dashboardPhotoData, setDashboardPhotoData] = useState(null);
  const [loadingDashboardPhoto, setLoadingDashboardPhoto] = useState(false);
  
  // Q&A Image viewer state
  const [showQAImageModal, setShowQAImageModal] = useState(false);
  const [qaImageData, setQaImageData] = useState(null);
  const [loadingQAImage, setLoadingQAImage] = useState(false);
  const [qaImageTitle, setQaImageTitle] = useState('');
  const [qaMediaType, setQaMediaType] = useState('image');
  
  // Helper to detect video URLs
  const isVideoUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('.mov') || lower.includes('.webm') || 
           lower.includes('.avi') || lower.includes('video') || lower.includes('/videos/');
  };

  // Convert GCS/Firebase URLs to a proper Firebase Storage download URL
  const toFirebaseDownloadUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    // Already a Firebase API URL
    if (url.includes('firebasestorage.googleapis.com/v0/')) return url;
    // Convert storage.googleapis.com/{bucket}/{path} -> Firebase API URL
    if (url.includes('storage.googleapis.com/')) {
      try {
        const u = new URL(url);
        const parts = u.pathname.split('/').filter(Boolean); // ['bucket', 'path', 'to', 'file']
        if (parts.length >= 2) {
          const bucket = parts[0];
          const objectPath = parts.slice(1).join('/');
          const encoded = encodeURIComponent(objectPath);
          return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encoded}?alt=media`;
        }
      } catch (e) { /* fall through */ }
    }
    // Convert gs://bucket/path -> Firebase API URL
    if (url.startsWith('gs://')) {
      const withoutGs = url.replace('gs://', '');
      const bucket = withoutGs.split('/')[0];
      const objectPath = withoutGs.split('/').slice(1).join('/');
      const encoded = encodeURIComponent(objectPath);
      return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encoded}?alt=media`;
    }
    return url;
  };

  // Close lightbox on Escape key
  useEffect(() => {
    if (!showQAImageModal) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') { setShowQAImageModal(false); setQaImageData(null); }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showQAImageModal]);
  
  // Save to Master Modal state
  const [saveToMasterRepair, setSaveToMasterRepair] = useState(null);
  
  // Rating Breakdown Modal state
  const [showRatingBreakdownModal, setShowRatingBreakdownModal] = useState(false);
  const [ratingHistory, setRatingHistory] = useState([]);
  
  // Function to view Q&A image/video (handles both URLs and media_ref)
  const viewQAImage = async (mediaUrl, title = 'Q&A Photo', mediaType = 'image') => {
    setQaImageTitle(title);
    setShowQAImageModal(true);
    // Store media type hint so modal knows whether to render video or image
    setQaMediaType(mediaType);
    
    if (mediaUrl?.startsWith('media_ref:')) {
      setLoadingQAImage(true);
      setQaImageData(null);
      try {
        const mediaRefId = mediaUrl.replace('media_ref:', '');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inspection-media/${mediaRefId}`);
        if (response.data?.data) {
          setQaImageData(response.data.data);
        }
      } catch (error) {
        console.error('Failed to load Q&A image:', error);
        toast.error('Failed to load image');
      } finally {
        setLoadingQAImage(false);
      }
    } else if (mediaUrl) {
      // For video URLs, resolve through backend to get a working signed/CDN URL
      if (mediaType === 'video' && mediaUrl && !mediaUrl.startsWith('data:')) {
        setLoadingQAImage(true);
        setQaImageData(null);
        try {
          const resolveResp = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/media/resolve-video-url?url=${encodeURIComponent(mediaUrl)}`
          );
          if (resolveResp.data?.url) {
            setQaImageData(resolveResp.data.url);
          } else {
            setQaImageData(mediaUrl);
          }
        } catch (err) {
          console.error('Failed to resolve video URL:', err);
          setQaImageData(mediaUrl);
        } finally {
          setLoadingQAImage(false);
        }
      } else {
        setQaImageData(mediaUrl);
      }
    }
  };
  
  // Helper to find dashboard odometer photo for THIS inspection.
  const getDashboardOdometerPhoto = useCallback(() => {
    // 1. Prefer the inspection's OWN answer (correct question ids) so we never
    //    surface a stale/foreign photo from a previously-opened inspection.
    const dashQids = [
      'f93f7cea-9664-4e67-8f42-075e4d990a5e',
      'd72d0a59-f621-4afa-8f2e-191f2efe31ef',
      '3ca03df7-458b-4da4-b0d9-f938614c4a12',
    ];
    const answers = inspection?.inspection_answers || {};
    for (const qid of dashQids) {
      const a = answers[qid];
      if (a) {
        const url = typeof a === 'string' ? a : a?.answer;
        if (url) return url;
      }
    }

    // 2. Fall back to the previously extracted image (set by the backend).
    if (inspection?.ai_extracted_kms_image_url) return inspection.ai_extracted_kms_image_url;

    // 3. Last resort: liveProgressData categories for "Dashboard Odometer Photo"
    const categories = liveProgressData?.categories || [];
    for (const cat of categories) {
      const questions = cat.questions || [];
      const questionsArr = Array.isArray(questions) ? questions : Object.values(questions);
      for (const q of questionsArr) {
        const qtext = (q.question_text || '').toLowerCase();
        if ((qtext.includes('dashboard') && qtext.includes('odometer')) || qtext === 'dashboard odometer photo') {
          const answer = q.answer;
          if (answer && typeof answer === 'string' && (answer.startsWith('data:image') || answer.includes('http') || answer.includes('firebase') || answer.includes('media_ref:'))) {
            return answer;
          }
        }
      }
    }

    return null;
  }, [inspection, liveProgressData]);
  
  // Load dashboard photo when modal opens
  useEffect(() => {
    const loadDashboardPhoto = async () => {
      if (!showDashboardPhotoModal) return;
      
      const imageUrl = getDashboardOdometerPhoto();
      
      if (!imageUrl) {
        setDashboardPhotoData(null);
        return;
      }
      
      if (imageUrl.startsWith('media_ref:')) {
        setLoadingDashboardPhoto(true);
        try {
          const mediaRefId = imageUrl.replace('media_ref:', '');
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inspection-media/${mediaRefId}`);
          if (response.data?.data) {
            setDashboardPhotoData(response.data.data);
          }
        } catch (error) {
          console.error('Failed to load dashboard photo:', error);
        } finally {
          setLoadingDashboardPhoto(false);
        }
      } else {
        setDashboardPhotoData(imageUrl);
      }
    };
    
    loadDashboardPhoto();
  }, [showDashboardPhotoModal, getDashboardOdometerPhoto]);
  
  // Auto-fetch challans ONLY when user is on the Vehicle tab AND we don't
  // have data yet. P0 cost optimization (Feb 2026): previously fired on
  // every modal open regardless of which tab the user landed on (OBD,
  // Reports, Repairs etc.) — every fresh inspection cost {activeCurrencySymbol()}4 the moment
  // someone opened the modal, even if they never wanted challan data.
  // Backend is also cache-first now (cross-inspection by rc_number, 30d
  // TTL), so this only triggers a paid Surepass call for genuinely new
  // vehicles.
  useEffect(() => {
    if (
      isOpen &&
      activeTab === 'vehicle' &&
      inspection?.id &&
      inspection?.car_number &&
      !inspection?.challan_data
    ) {
      inspectionsApi.fetchChallans(inspection.id)
        .then(response => {
          if (response.data?.success && onInspectionUpdated) {
            onInspectionUpdated({ ...inspection, challan_data: response.data.data });
          }
        })
        .catch(() => {}); // Silent - user can manually retry via button
    }
  }, [isOpen, activeTab, inspection?.id]);
  
  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  
  // Missing data validation state
  const [missingDataItems, setMissingDataItems] = useState([]);
  const [showMissingDataAlert, setShowMissingDataAlert] = useState(false);
  
  // Function to validate and identify missing data across all tabs
  const validateMissingData = () => {
    const missing = [];
    
    // Check Vehicle Details
    if (!editData.vehicle_make || editData.vehicle_make === '') {
      missing.push({ field: 'Vehicle Make', tab: 'Summary', priority: 'high' });
    }
    if (!editData.vehicle_model || editData.vehicle_model === '') {
      missing.push({ field: 'Vehicle Model', tab: 'Summary', priority: 'high' });
    }
    if (!editData.vehicle_year || editData.vehicle_year === '') {
      missing.push({ field: 'Manufacturing Year', tab: 'Summary', priority: 'high' });
    }
    if (!editData.fuel_type || editData.fuel_type === '') {
      missing.push({ field: 'Fuel Type', tab: 'Summary', priority: 'medium' });
    }
    if (!editData.transmission || editData.transmission === '') {
      missing.push({ field: 'Transmission', tab: 'Summary', priority: 'medium' });
    }
    if (!editData.kms_driven || editData.kms_driven === 0) {
      missing.push({ field: 'KMs Driven', tab: 'Summary', priority: 'high' });
    }
    
    // Check Conditions
    if (!editData.engine_condition || editData.engine_condition === 'PENDING') {
      missing.push({ field: 'Engine Condition', tab: 'Summary', priority: 'high' });
    }
    if (!editData.exterior_condition || editData.exterior_condition === 'PENDING') {
      missing.push({ field: 'Exterior Condition', tab: 'Summary', priority: 'high' });
    }
    if (!editData.interior_condition || editData.interior_condition === 'PENDING') {
      missing.push({ field: 'Interior Condition', tab: 'Summary', priority: 'high' });
    }
    if (!editData.transmission_condition || editData.transmission_condition === 'PENDING') {
      missing.push({ field: 'Transmission Condition', tab: 'Summary', priority: 'medium' });
    }
    
    // Check Market Value
    if (!editData.market_value_min || editData.market_value_min === 0) {
      missing.push({ field: 'Market Value (Min)', tab: 'Summary', priority: 'high' });
    }
    if (!editData.market_value_max || editData.market_value_max === 0) {
      missing.push({ field: 'Market Value (Max)', tab: 'Summary', priority: 'high' });
    }
    
    // Check Overall Rating
    if (!editData.overall_rating || editData.overall_rating === 0) {
      missing.push({ field: 'Overall Rating', tab: 'Summary', priority: 'high' });
    }
    
    // Check Q&A completion from live progress
    const categories = liveProgressData?.categories || [];
    categories.forEach(cat => {
      if (cat.is_required && cat.progress < 100) {
        missing.push({ 
          field: `${cat.category_name} (${cat.progress}% complete)`, 
          tab: 'Q&A Progress', 
          priority: 'high' 
        });
      }
    });
    
    // Check OBD Data
    if (!liveProgressData?.obd_scan?.completed) {
      missing.push({ field: 'OBD Scan', tab: 'OBD-2 Scanner', priority: 'medium' });
    }
    
    // Check Insurance
    if (!editData.insurance_status || editData.insurance_status === 'PENDING') {
      missing.push({ field: 'Insurance Status', tab: 'Summary', priority: 'medium' });
    }
    
    // Check RTO Verification
    if (!editData.rto_verification_status || editData.rto_verification_status === 'PENDING') {
      missing.push({ field: 'RTO Verification', tab: 'Summary', priority: 'low' });
    }
    
    return missing;
  };
  
  // Track ALL manually edited fields to prevent auto-refresh from overwriting user changes
  // Using ref with a Set instead of state to avoid stale closure issues
  // Per-field protection: only the specific field edited is protected
  const manuallyEditedFieldsRef = useRef(new Set());
  // Tracks inspection ids we've already auto-fetched Vaahan for, so the 5s
  // auto-refresh can never re-trigger the fetch (was looping: repeated
  // "Vehicle data loaded" toast + vehicle/reg flicker).
  const vaahanFetchAttemptedRef = useRef(new Set());
  
  // Helper to check if a field was manually edited
  const isFieldManuallyEdited = (field) => manuallyEditedFieldsRef.current.has(field);
  
  // Helper to mark a field as manually edited
  const markFieldAsEdited = (field) => {
    manuallyEditedFieldsRef.current.add(field);
    console.log(`[ManualEdit] Field marked as edited: ${field}`, Array.from(manuallyEditedFieldsRef.current));
  };
  
  // Helper to check if ANY field has been manually edited (for complete refresh skip)
  const hasAnyManualEdits = () => manuallyEditedFieldsRef.current.size > 0;
  
  // Editable data state
  const [editData, setEditData] = useState({
    // Header & Assessment
    overall_rating: 0,
    overall_rating_locked: false, // When true, overall_rating won't auto-recalculate from category ratings
    recommended_to_buy: false,
    market_value_min: 0,
    market_value_max: 0,
    assessment_summary: '',
    key_highlights: [],
    
    // Vehicle Details
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    fuel_type: '',
    transmission: '',
    vehicle_colour: '',
    engine_cc: 0,
    engine_number: '',
    chassis_number: '',
    kms_driven: 0,
    owners: 0,
    
    // Condition Ratings (AI + Manual)
    engine_condition: 'PENDING',
    interior_condition: 'PENDING',
    exterior_condition: 'PENDING',
    transmission_condition: 'PENDING',
    
    // Key Information
    accident_history: false,
    flood_damage: false,
    dents_scratches: false,
    
    // Insurance
    insurance_status: '',
    insurer_name: '',
    policy_number: '',
    insurance_expiry: '',
    policy_type: '',
    idv_value: 0,
    
    // Repairs Estimation (NEW)
    repairs: [],
    total_repair_cost_min: 0,
    total_repair_cost_max: 0,
    
    // Additional Vehicle fields
    car_number: '',
    body_type: '',
    vehicle_class: '',
    cylinders: 0,
    seating_capacity: 0,
    emission_norms: '',
    gross_weight: '',
    unladen_weight: '',
    wheelbase: '',
    
    // Registration & Ownership
    manufacturing_date: '',
    registration_date: '',
    rc_expiry_date: '',
    registration_authority: '',
    owner_name: '',
    
    // RTO Verification
    rto_verification_status: 'PENDING',
    hypothecation: '',
    blacklist_status: false,
    is_financed: false,
    tax_valid_upto: '',
    fitness_upto: '',
    pucc_number: '',
    pucc_valid_upto: '',
    
    // OBD
    obd_connected: false,
    dtc_codes: [],
    
    // Category Ratings (0-10 scale, editable)
    category_ratings: {},
    
    // Overall Rating History (audit log)
    overall_rating_history: []
  });
  
  // Initialize edit data from inspection and live progress - only when inspection ID changes
  const [initializedInspectionId, setInitializedInspectionId] = useState(null);
  
  useEffect(() => {
    // Only initialize once per inspection ID to prevent resetting user edits
    if (!inspection || !liveProgressData || initializedInspectionId === inspection.id) return;
    
    // Handle assessment_summary - it might be an object or a string
    let assessmentSummary = inspection.assessment_summary || '';
    if (typeof assessmentSummary === 'object' && assessmentSummary !== null) {
      assessmentSummary = assessmentSummary.overall || '';
    }
    
    // Get market values from multiple possible sources
    const aiInsights = inspection.ai_insights || {};
    const marketValueMin = inspection.market_value_min || aiInsights.market_value?.min || 0;
    const marketValueMax = inspection.market_value_max || aiInsights.market_value?.max || 0;
    
    // Get overall rating - handle 0 as valid value
    const overallRating = inspection.overall_rating !== undefined && inspection.overall_rating !== null 
      ? inspection.overall_rating 
      : (aiInsights.overall_rating !== undefined && aiInsights.overall_rating !== null 
        ? aiInsights.overall_rating 
        : (liveProgressData?.ai_report?.overall_rating || 0));
    
    // Get fuel type from multiple sources and normalize to match dropdown options
    const rawFuelType = inspection.fuel_type || inspection.vaahan_data?.fuel_type || '';
    let fuelType = '';
    if (rawFuelType) {
      const upperFuel = rawFuelType.toUpperCase();
      if (upperFuel.includes('PETROL')) fuelType = 'Petrol';
      else if (upperFuel.includes('DIESEL')) fuelType = 'Diesel';
      else if (upperFuel.includes('CNG')) fuelType = 'CNG';
      else if (upperFuel.includes('LPG')) fuelType = 'LPG';
      else if (upperFuel.includes('ELECTRIC') || upperFuel.includes('BEV')) fuelType = 'Electric';
      else if (upperFuel.includes('HYBRID') || upperFuel.includes('EV')) fuelType = 'Hybrid';
      else fuelType = rawFuelType.charAt(0).toUpperCase() + rawFuelType.slice(1).toLowerCase();
    }
    
    // Get transmission - try inspection first, then extract from model name
    let rawTransmission = inspection.transmission || '';
    if (!rawTransmission) {
      // Extract transmission from model name if not set
      const model = (inspection.vaahan_data?.model || '').toUpperCase();
      
      // Helper to check if model contains automatic transmission code
      const isAutomatic = (modelStr) => {
        // Longer unique codes - can match anywhere (no false positive risk)
        const uniqueCodes = ['AUTOMATIC', 'XTRONIC', 'TIPTRONIC', 'STEPTRONIC', 'E-CVT', 'ECVT'];
        if (uniqueCodes.some(code => modelStr.includes(code))) return true;
        
        // Short codes - need word boundary to avoid false positives (e.g., "AT" in "SEAT")
        // Boundaries: space, hyphen, underscore, parentheses, or end of string
        const shortCodes = ['AT', 'AMT', 'CVT', 'IVT', 'DCT', 'DSG', 'AGS', 'IMT', 'TC', 'AUTO', 'DCA', 'TCT'];
        for (const code of shortCodes) {
          const patterns = [` ${code}`, `-${code}`, `_${code}`, `(${code})`];
          if (patterns.some(p => modelStr.includes(p))) return true;
          if (modelStr.endsWith(code) || modelStr.endsWith(`${code})`)) return true;
        }
        return false;
      };
      
      // If automatic pattern found → Automatic, else → Manual
      rawTransmission = isAutomatic(model) ? 'Automatic' : 'Manual';
    }
    // Normalize transmission — all automatic variants map to "Automatic"
    let transmission = '';
    if (rawTransmission) {
      const upperTrans = rawTransmission.toUpperCase();
      const automaticCodes = ['AT', 'AMT', 'CVT', 'IVT', 'DCT', 'DSG', 'AGS', 'IMT', 'TC', 'AUTO', 'AUTOMATIC', 'DCA', 'TCT', 'XTRONIC', 'TIPTRONIC', 'STEPTRONIC', 'E-CVT', 'ECVT', 'TORQUE CONVERTER'];
      if (automaticCodes.includes(upperTrans) || upperTrans === 'AUTOMATIC') {
        transmission = 'Automatic';
      } else {
        transmission = 'Manual';
      }
    }
    
    setEditData({
      // From AI insights
      overall_rating: overallRating,
      overall_rating_locked: inspection.overall_rating_locked || false, // Load lock state from DB
      recommended_to_buy: inspection.recommended_to_buy ?? aiInsights.recommended_to_buy ?? liveProgressData?.ai_report?.recommended_to_buy ?? false,
      market_value_min: marketValueMin,
      market_value_max: marketValueMax,
      assessment_summary: assessmentSummary,
      key_highlights: inspection.key_highlights || aiInsights.key_highlights || [],
      
      // Vehicle
      vehicle_make: inspection.vehicle_make || inspection.vaahan_data?.manufacturer || '',
      vehicle_model: inspection.vehicle_model || inspection.vaahan_data?.model || '',
      vehicle_year: inspection.vehicle_year || '',
      fuel_type: fuelType,
      transmission: transmission,
      vehicle_colour: inspection.vehicle_colour || inspection.vaahan_data?.color || '',
      engine_cc: inspection.engine_cc || 0,
      engine_number: inspection.engine_number || inspection.engine_no || '',
      chassis_number: inspection.chassis_number || inspection.chassis_no || '',
      kms_driven: inspection.kms_driven || 0,
      owners: inspection.owners || parseInt(inspection.vaahan_data?.owner_count) || 0,
      
      // Conditions — prefer DB value if set (not PENDING), else fall back to AI insights
      // normalizeCondition converts FAIR -> AVERAGE for consistency
      engine_condition: normalizeCondition((() => {
        if (inspection.engine_condition && inspection.engine_condition !== 'PENDING') return inspection.engine_condition;
        const aiCond = inspection.ai_insights?.condition_ratings;
        return aiCond?.engine || 'PENDING';
      })()),
      interior_condition: normalizeCondition((() => {
        if (inspection.interior_condition && inspection.interior_condition !== 'PENDING') return inspection.interior_condition;
        const aiCond = inspection.ai_insights?.condition_ratings;
        return aiCond?.interior || 'PENDING';
      })()),
      exterior_condition: normalizeCondition((() => {
        if (inspection.exterior_condition && inspection.exterior_condition !== 'PENDING') return inspection.exterior_condition;
        const aiCond = inspection.ai_insights?.condition_ratings;
        return aiCond?.exterior || 'PENDING';
      })()),
      transmission_condition: normalizeCondition((() => {
        if (inspection.transmission_condition && inspection.transmission_condition !== 'PENDING') return inspection.transmission_condition;
        const aiCond = inspection.ai_insights?.condition_ratings;
        return aiCond?.transmission || 'PENDING';
      })()),
      
      // Key Info
      accident_history: inspection.accident_history || false,
      flood_damage: inspection.flood_damage || false,
      dents_scratches: inspection.dents_scratches || false,
      
      // Insurance — determine Active/Expired from expiry date
      insurance_status: (() => {
        const saved = inspection.insurance_status;
        if (saved && saved !== 'Unknown' && saved !== '') return saved;
        const expiryDate = inspection.insurance_expiry || inspection.vaahan_data?.insurance_valid_upto;
        if (expiryDate) {
          return new Date(expiryDate) > new Date() ? 'Active' : 'Expired';
        }
        return '';
      })(),
      insurer_name: inspection.insurer_name || '',
      policy_number: inspection.policy_number || '',
      insurance_expiry: inspection.insurance_expiry || '',
      policy_type: inspection.policy_type || '',
      idv_value: inspection.idv_value || 0,
      
      // Repairs
      repairs: inspection.repairs || [],
      total_repair_cost_min: inspection.total_repair_cost_min || 0,
      total_repair_cost_max: inspection.total_repair_cost_max || 0,
      
      // RTO
      rto_verification_status: inspection.rto_verification_status || 'PENDING',
      hypothecation: inspection.hypothecation || '',
      blacklist_status: inspection.blacklist_status || false,
      is_financed: inspection.is_financed ?? inspection.vaahan_data?.financed ?? false,
      tax_valid_upto: inspection.tax_valid_upto || inspection.vaahan_data?.tax_valid_upto || '',
      fitness_upto: inspection.fitness_upto || inspection.vaahan_data?.fitness_upto || '',
      pucc_number: inspection.pucc_number || inspection.vaahan_data?.pucc_number || '',
      pucc_valid_upto: inspection.pucc_valid_upto || inspection.vaahan_data?.pucc_valid_upto || '',
      
      // Additional Vehicle fields
      car_number: inspection.car_number || '',
      body_type: inspection.body_type || inspection.vaahan_data?.body_type || '',
      vehicle_class: inspection.vehicle_class || inspection.vaahan_data?.vehicle_class || '',
      cylinders: inspection.cylinders || parseInt(inspection.vaahan_data?.cylinders) || 0,
      seating_capacity: inspection.seating_capacity || parseInt(inspection.vaahan_data?.seating_capacity) || 0,
      emission_norms: inspection.emission_norms || inspection.vaahan_data?.emission_norms || '',
      gross_weight: inspection.gross_weight || inspection.vaahan_data?.gross_weight || '',
      unladen_weight: inspection.unladen_weight || inspection.vaahan_data?.unladen_weight || '',
      wheelbase: inspection.wheelbase || inspection.vaahan_data?.wheelbase || '',
      manufacturing_date: inspection.manufacturing_date || inspection.vaahan_data?.manufacturing_date || '',
      registration_date: inspection.registration_date || inspection.vaahan_data?.registration_date || '',
      rc_expiry_date: inspection.rc_expiry_date || inspection.vaahan_data?.rc_expiry_date || '',
      registration_authority: inspection.registration_authority || inspection.vaahan_data?.registration_authority || '',
      owner_name: inspection.owner_name || inspection.vaahan_data?.owner_name || '',
      
      // OBD
      obd_connected: liveProgressData?.obd_scan?.completed || false,
      dtc_codes: inspection.dtc_codes || [],
      
      // Category Ratings (0-10 scale) - Load from saved inspection data or empty
      // Normalize: DB may have {key: {rating: N, status: S}} or {key: N}
      // Also create normalized keys so slider values can be looked up by both original and normalized keys
      // IMPORTANT: Include ALL categories from template, not just DB-saved ones
      category_ratings: (() => {
        const result = {};
        
        // First, load from DB
        Object.entries(inspection.category_ratings || {}).forEach(([k, v]) => {
          const rating = typeof v === 'object' && v !== null ? (v.rating ?? 0) : (v ?? 0);
          result[k] = rating;
          // Also store under normalized key for cross-referencing with live-progress categories
          const normalizedKey = k.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
          if (normalizedKey !== k && !(normalizedKey in result)) {
            result[normalizedKey] = rating;
          }
        });
        
        // Then, add AI ratings for categories not already in result
        const aiRatings = inspection.ai_insights?.category_ratings || {};
        Object.entries(aiRatings).forEach(([k, v]) => {
          const normalizedKey = k.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
          if (!(k in result) && !(normalizedKey in result)) {
            const rating = typeof v === 'object' && v !== null ? (v.rating ?? v) : (v ?? 0);
            result[normalizedKey] = rating;
          }
        });
        
        // Finally, for categories in template that have answered questions but no saved rating,
        // calculate auto-rating. This happens during render in QATab but we do it here for Overall calc.
        const categories = liveProgressData?.categories || [];
        categories.forEach(cat => {
          const categoryKey = cat.category_name?.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
          if (!(categoryKey in result)) {
            const questions = cat.questions || [];
            const answered = questions.filter(q => q.is_answered);
            if (answered.length > 0) {
              const goodCount = answered.filter(q => {
                const a = (typeof q.answer === 'string' ? q.answer : '').toLowerCase();
                return !a.includes('poor') && !a.includes('bad') && !a.includes('damaged') && !a.includes('broken') && !a.includes('not working') && !a.includes('faulty') && !a.includes('average') && !a.includes('fair') && !a.includes('needs attention') && !a.includes('worn') && !a.includes('minor');
              }).length;
              result[categoryKey] = Math.round((goodCount / answered.length) * 10);
            }
          }
        });
        
        return result;
      })(),
      
      // Overall Rating History (audit log)
      overall_rating_history: inspection.overall_rating_history || []
    });
    
    // Auto-calculate overall_rating and conditions from category ratings
    const catRatings = Object.fromEntries(
      Object.entries(inspection.category_ratings || {}).map(([k, v]) => [
        k, typeof v === 'object' && v !== null ? (v.rating ?? 0) : (v ?? 0)
      ])
    );
    
    // Also normalize keys to match how QATab generates them
    const normalizedCatRatings = {};
    Object.entries(catRatings).forEach(([k, v]) => {
      const normalizedKey = k.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      normalizedCatRatings[normalizedKey] = v;
    });
    
    const categories = liveProgressData?.categories || [];
    if (categories.length > 0 && Object.keys(normalizedCatRatings).length > 0) {
      const conditionUpdates = calculateConditionsFromRatings(normalizedCatRatings, categories);
      if (Object.keys(conditionUpdates).length > 0) {
        // Only update conditions on INITIAL load (not on every liveProgressData change)
        // NEVER update overall_rating here - user controls that
        const isInitialLoad = initializedInspectionId !== inspection.id;
        const { overall_rating: calculatedRating, ...conditionsOnly } = conditionUpdates;
        if (Object.keys(conditionsOnly).length > 0) {
          setEditData(prev => ({ ...prev, ...conditionsOnly }));
        }
      }
    } else {
      // Fallback: calculate from any available ratings even without liveProgressData
      // NEVER auto-update overall_rating here - only conditions
      
      // Try to calculate conditions using category_ratings keys directly (from DB)
      // Only non-zero ratings affect condition groups (0 = not yet rated for that group)
      const dbGroups = { engine: [], exterior: [], interior: [], transmission: [] };
      Object.entries(catRatings).forEach(([k, v]) => {
        if (v > 0) {
          const group = mapCategoryToGroup(k);
          if (group) dbGroups[group].push(v);
        }
      });
      const avg = (arr) => arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null;
      const updates = {};
      const eA = avg(dbGroups.engine); if (eA !== null) updates.engine_condition = ratingToCondition(eA);
      const exA = avg(dbGroups.exterior); if (exA !== null) updates.exterior_condition = ratingToCondition(exA);
      const iA = avg(dbGroups.interior); if (iA !== null) updates.interior_condition = ratingToCondition(iA);
      const tA = avg(dbGroups.transmission); if (tA !== null) updates.transmission_condition = ratingToCondition(tA);
      if (Object.keys(updates).length > 0) {
        setEditData(prev => ({ ...prev, ...updates }));
      }
    }
    
    // Store original data for change tracking
    setOriginalData(JSON.stringify({
      overall_rating: overallRating,
      recommended_to_buy: inspection.recommended_to_buy || false,
      market_value_min: marketValueMin,
      market_value_max: marketValueMax,
      assessment_summary: assessmentSummary,
      repairs: inspection.repairs || []
    }));
    setHasUnsavedChanges(false);
    setInitializedInspectionId(inspection.id);
  }, [inspection?.id, liveProgressData, initializedInspectionId]);
  
  // RECOVERY (P0 repairs data-loss guard): if the modal initialized before the
  // authoritative inspection.repairs arrived (e.g. a getById race / partial data),
  // editData.repairs would be stuck empty and a later save would wipe the DB list.
  // Backfill from inspection.repairs once it shows up — but NEVER clobber the
  // curator's in-session repair edits.
  useEffect(() => {
    if (initializedInspectionId !== inspection?.id) return;
    if (isFieldManuallyEdited('repairs')) return;
    const dbRepairs = inspection?.repairs;
    if (!Array.isArray(dbRepairs) || dbRepairs.length === 0) return;
    setEditData(prev => {
      if ((prev.repairs?.length || 0) > 0) return prev; // already have repairs — leave as-is
      return {
        ...prev,
        repairs: dbRepairs,
        total_repair_cost_min: inspection.total_repair_cost_min || prev.total_repair_cost_min,
        total_repair_cost_max: inspection.total_repair_cost_max || prev.total_repair_cost_max,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection?.id, inspection?.repairs, initializedInspectionId]);
  
  // Track changes
  useEffect(() => {
    if (originalData) {
      const currentData = JSON.stringify({
        overall_rating: editData.overall_rating,
        recommended_to_buy: editData.recommended_to_buy,
        market_value_min: editData.market_value_min,
        market_value_max: editData.market_value_max,
        assessment_summary: editData.assessment_summary,
        repairs: editData.repairs
      });
      setHasUnsavedChanges(currentData !== originalData);
    }
  }, [editData, originalData]);
  
  // Recalculate conditions when liveProgressData categories become available
  // This handles the race condition where init runs before categories are loaded
  // IMPORTANT: Don't overwrite values that were saved by user
  useEffect(() => {
    const categories = liveProgressData?.categories || [];
    const catRatings = editData.category_ratings || {};
    if (categories.length > 0 && Object.keys(catRatings).length > 0) {
      const conditionUpdates = calculateConditionsFromRatings(catRatings, categories);
      if (Object.keys(conditionUpdates).length > 0) {
        setEditData(prev => {
          // Filter out manually edited fields AND fields with valid saved values
          const filteredUpdates = {};
          Object.entries(conditionUpdates).forEach(([key, value]) => {
            // Skip if manually edited
            if (isFieldManuallyEdited(key)) {
              console.log(`[LiveModal] Skipping ${key} recalculation - user has manual edit`);
              return;
            }
            // Skip overall_rating if it already has a valid saved value (not 0)
            if (key === 'overall_rating' && prev.overall_rating > 0) {
              console.log(`[LiveModal] Skipping overall_rating recalculation - already has value: ${prev.overall_rating}`);
              return;
            }
            // Skip condition fields if they already have a non-PENDING saved value
            // This respects user's saved condition overrides
            if (key.endsWith('_condition') && prev[key] && prev[key] !== 'PENDING') {
              console.log(`[LiveModal] Skipping ${key} recalculation - already has value: ${prev[key]}`);
              return;
            }
            filteredUpdates[key] = value;
          });
          
          // Only update if there are changes
          const changed = Object.entries(filteredUpdates).some(([k, v]) => prev[k] !== v);
          if (!changed) return prev;
          return { ...prev, ...filteredUpdates };
        });
      }
    }
  }, [liveProgressData?.categories, editData.category_ratings]);
  
  // When full inspection data arrives (from getById), merge category_ratings into editData
  // This handles the case where the modal opened with list data (missing category_ratings)
  // and the full data arrives later
  useEffect(() => {
    if (!inspection?.category_ratings || !initializedInspectionId) return;
    const inspCR = inspection.category_ratings;
    if (typeof inspCR !== 'object' || Object.keys(inspCR).length === 0) return;
    
    setEditData(prev => {
      const prevCR = prev.category_ratings || {};
      
      // Merge: normalize DB values and combine with any user changes
      const merged = { ...prevCR };
      let hasNewKeys = false;
      Object.entries(inspCR).forEach(([k, v]) => {
        const rating = typeof v === 'object' && v !== null ? (v.rating ?? 0) : (v ?? 0);
        if (!(k in merged)) {
          merged[k] = rating;
          hasNewKeys = true;
        }
        // Also add normalized key
        const normalizedKey = k.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
        if (normalizedKey !== k && !(normalizedKey in merged)) {
          merged[normalizedKey] = rating;
          hasNewKeys = true;
        }
      });
      
      if (!hasNewKeys) return prev;
      return { ...prev, category_ratings: merged };
    });
  }, [inspection?.category_ratings, initializedInspectionId]);
  
  // Update condition fields and kms_driven when full inspection data arrives
  // (list API may omit these fields; full getById data includes them)
  // Also recalculate conditions from ratings if they seem incorrect
  // NOW: Respects per-field manual edit protection
  useEffect(() => {
    if (!inspection || !initializedInspectionId) return;
    
    setEditData(prev => {
      const updates = {};
      const aiCond = inspection.ai_insights?.condition_ratings || {};
      // Merge condition fields: prefer non-PENDING DB value, then AI insights
      // BUT skip if field was manually edited
      const resolve = (dbVal, aiVal) => (dbVal && dbVal !== 'PENDING') ? normalizeCondition(dbVal) : (normalizeCondition(aiVal) || null);
      
      // Only update condition fields if not manually edited
      const eCond = resolve(inspection.engine_condition, aiCond.engine);
      if (eCond && prev.engine_condition === 'PENDING' && !isFieldManuallyEdited('engine_condition')) 
        updates.engine_condition = eCond;
      const exCond = resolve(inspection.exterior_condition, aiCond.exterior);
      if (exCond && prev.exterior_condition === 'PENDING' && !isFieldManuallyEdited('exterior_condition')) 
        updates.exterior_condition = exCond;
      const iCond = resolve(inspection.interior_condition, aiCond.interior);
      if (iCond && prev.interior_condition === 'PENDING' && !isFieldManuallyEdited('interior_condition')) 
        updates.interior_condition = iCond;
      const tCond = resolve(inspection.transmission_condition, aiCond.transmission);
      if (tCond && prev.transmission_condition === 'PENDING' && !isFieldManuallyEdited('transmission_condition')) 
        updates.transmission_condition = tCond;
      
      // Merge overall_rating if not set AND not manually edited
      if (inspection.overall_rating && inspection.overall_rating > 0 && prev.overall_rating === 0 && !isFieldManuallyEdited('overall_rating'))
        updates.overall_rating = inspection.overall_rating;
      
      // Merge kms_driven if not manually set AND not manually edited
      if (inspection.kms_driven && inspection.kms_driven > 0 && prev.kms_driven === 0 && !isFieldManuallyEdited('kms_driven'))
        updates.kms_driven = inspection.kms_driven;
      
      // CRITICAL: Recalculate conditions from category_ratings ONLY if DB value is PENDING
      // Don't override user-saved values with auto-calculated ones
      // Users may intentionally set a condition different from the calculated average
      const catRatings = { ...prev.category_ratings, ...inspection.category_ratings };
      if (Object.keys(catRatings).length > 0) {
        const calculatedConds = calculateConditionsFromRatings(catRatings, liveProgressData?.categories || []);
        // Only apply calculated conditions if:
        // 1. User hasn't manually edited this condition
        // 2. Current value is PENDING (no saved value yet)
        ['engine', 'exterior', 'interior', 'transmission'].forEach(group => {
          const condField = `${group}_condition`;
          if (isFieldManuallyEdited(condField)) {
            console.log(`[LiveModal] Skipping ${condField} update - user has manual edit`);
            return;
          }
          
          const calcCond = calculatedConds[condField];
          const currentCond = updates[condField] || prev[condField];
          const dbCond = inspection[condField];
          
          // IMPORTANT: If DB has a non-PENDING value, TRUST it (user saved it intentionally)
          // Only auto-calculate if current value is PENDING
          if (dbCond && dbCond !== 'PENDING') {
            // DB has a saved value - don't override
            console.log(`[LiveModal] Keeping saved ${condField}: ${dbCond} (not overriding with calculated)`);
            if (currentCond !== dbCond) {
              updates[condField] = normalizeCondition(dbCond);
            }
          } else if (calcCond && calcCond !== 'PENDING' && currentCond === 'PENDING') {
            // Only auto-calculate if current is PENDING
            console.log(`[LiveModal] Auto-calculating ${condField}: PENDING -> ${calcCond} (based on ratings)`);
            updates[condField] = calcCond;
          }
        });
        // DO NOT auto-update overall_rating here - let user manually edit or use Refresh button
        // The useEffect was causing issues by overwriting user edits
      }
      
      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
  }, [inspection?.engine_condition, inspection?.exterior_condition, inspection?.interior_condition, inspection?.transmission_condition, inspection?.overall_rating, inspection?.kms_driven, inspection?.ai_insights, inspection?.category_ratings, initializedInspectionId, liveProgressData?.categories]);

  
  // Fetch repair parts and rules on mount
  useEffect(() => {
    const fetchRepairsData = async () => {
      if (!isOpen) return;
      setLoadingRepairs(true);
      try {
        const [partsRes, rulesRes, categoriesRes, questionsRes] = await Promise.all([
          repairsApi.getParts(),
          repairsApi.getRules(),
          repairsApi.getPartCategories(),
          repairsApi.getInspectionQuestions()
        ]);
        setRepairParts(partsRes.data || []);
        setRepairRules(rulesRes.data || []);
        setPartCategories(categoriesRes.data || []);
        setInspectionQuestions(questionsRes.data || []);
      } catch (err) {
        console.error('Failed to load repairs data:', err);
      } finally {
        setLoadingRepairs(false);
      }
    };
    fetchRepairsData();
  }, [isOpen]);
  
  // Auto-refresh effect - respects manual edits (per-field protection).
  // 2026-09-21 (P0): this used to poll every 5s unconditionally — for a PUBLISHED
  // inspection nobody was working on, and with the browser tab in the background —
  // and every tick fires TWO API calls. A handful of forgotten open modals drained
  // the backend's Mongo connection pool and took the whole CRM and the mechanic app
  // down with it. Now: 5s only while a mechanic can still be working, 30s once the
  // inspection is finished, and nothing at all while the tab is hidden.
  const pollMs = LIVE_POLL_STATUSES.includes(inspection?.inspection_status) ? 5000 : 30000;
  useEffect(() => {
    if (!isOpen || !autoRefresh || !inspection?.id) return undefined;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      // Don't skip auto-refresh entirely - just skip updating manually edited fields
      // This allows other fields to continue receiving live updates from mechanic
      if (hasAnyManualEdits()) {
        console.log('[Auto-refresh] Manual edits detected, will preserve these fields:', Array.from(manuallyEditedFieldsRef.current));
      }
      onRefresh(inspection.id);
    }, pollMs);
    return () => clearInterval(interval);
  }, [isOpen, autoRefresh, inspection?.id, pollMs, onRefresh]);
  
  // Reset initialized inspection ID and manual edit flags when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInitializedInspectionId(null);
      manuallyEditedFieldsRef.current = new Set(); // Clear all manual edit tracking
      vaahanFetchAttemptedRef.current = new Set(); // Allow Vaahan re-fetch on next open
    }
  }, [isOpen]);
  
  // Auto-fetch Vaahan data when modal opens and vehicle details are empty
  useEffect(() => {
    const autoFetchVaahanData = async () => {
      // Only fetch if modal is open, we have an inspection ID, and vehicle details are missing
      if (!isOpen || !inspection?.id) return;
      
      // Check if vehicle details are missing
      const hasVehicleDetails = inspection.vehicle_make || inspection.vehicle_model || inspection.vaahan_data;
      if (hasVehicleDetails) return; // Already have vehicle data
      
      // Check if car number exists
      if (!inspection.car_number) return;
      
      // Fetch Vaahan at most ONCE per inspection open. The 5s auto-refresh
      // returns a fresh inspection object each tick, so without this guard the
      // effect re-ran and re-fetched every 5s (toast spam + vehicle/reg flicker).
      if (vaahanFetchAttemptedRef.current.has(inspection.id)) return;
      vaahanFetchAttemptedRef.current.add(inspection.id);
      
      try {
        const vaahanResponse = await inspectionsApi.fetchVaahanData(inspection.id);
        if (vaahanResponse.data?.success) {
          const updatedInspection = vaahanResponse.data.inspection || {};
          
          setEditData(prev => {
            // Normalize fuel_type from Vaahan
            const rawFuel = updatedInspection.fuel_type || prev.fuel_type || '';
            let normalizedFuel = prev.fuel_type;
            if (rawFuel) {
              const upperFuel = rawFuel.toUpperCase();
              if (upperFuel.includes('PETROL')) normalizedFuel = 'Petrol';
              else if (upperFuel.includes('DIESEL')) normalizedFuel = 'Diesel';
              else if (upperFuel.includes('CNG')) normalizedFuel = 'CNG';
              else if (upperFuel.includes('LPG')) normalizedFuel = 'LPG';
              else if (upperFuel.includes('ELECTRIC') || upperFuel.includes('EV') || upperFuel.includes('BEV')) normalizedFuel = 'Electric';
              else if (upperFuel.includes('HYBRID')) normalizedFuel = 'Hybrid';
              else normalizedFuel = rawFuel.charAt(0).toUpperCase() + rawFuel.slice(1).toLowerCase();
            }
            
            return {
              ...prev,
              vehicle_make: updatedInspection.vehicle_make || prev.vehicle_make,
              vehicle_model: updatedInspection.vehicle_model || prev.vehicle_model,
              vehicle_year: updatedInspection.vehicle_year || prev.vehicle_year,
              vehicle_colour: updatedInspection.vehicle_colour || prev.vehicle_colour,
              fuel_type: normalizedFuel,
              owners: updatedInspection.owners || prev.owners,
              insurer_name: updatedInspection.insurer_name || prev.insurer_name,
              insurance_expiry: updatedInspection.insurance_expiry || prev.insurance_expiry,
              insurance_status: updatedInspection.insurance_status || prev.insurance_status,
              policy_number: updatedInspection.policy_number || prev.policy_number,
              hypothecation: updatedInspection.hypothecation || prev.hypothecation,
              blacklist_status: updatedInspection.blacklist_status || prev.blacklist_status,
              rto_verification_status: updatedInspection.rto_verification_status || prev.rto_verification_status,
            };
          });
          
          if (onInspectionUpdated) {
            onInspectionUpdated(updatedInspection);
          }
          
          toast.success('Vehicle data loaded from Vaahan API');
        }
      } catch (err) {
        console.log('Auto Vaahan fetch failed:', err.message);
      }
    };
    
    autoFetchVaahanData();
    // NOTE: deliberately NOT depending on inspection.vehicle_make / vehicle_model
    // / vaahan_data — those change reference on every 5s refresh and caused this
    // effect to loop. The per-id ref guard above fires the fetch at most once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, inspection?.id, inspection?.car_number, onInspectionUpdated]);
  
  // Helper function to convert rating (0-10) to condition string
  // Logic: 8-10 = GOOD, 5-7 = AVERAGE, 1-4 = POOR
  const ratingToCondition = (rating) => {
    if (rating >= 8) return 'GOOD';
    if (rating >= 5) return 'AVERAGE';
    if (rating >= 1) return 'POOR';
    return 'PENDING';
  };
  
  // Normalize condition - convert FAIR to AVERAGE for consistency
  const normalizeCondition = (cond) => {
    if (!cond) return 'PENDING';
    const upper = cond.toUpperCase();
    // FAIR -> AVERAGE for consistency
    if (upper === 'FAIR') return 'AVERAGE';
    return upper;
  };
  
  // Map a Q&A category name to one of the 4 condition groups
  // Returns 'engine' | 'exterior' | 'interior' | 'transmission' | null
  const mapCategoryToGroup = (catName) => {
    const n = (catName || '').toLowerCase();
    // Transmission group (check first — "Transmission System Diagnosis" should be transmission, not engine)
    if (n.includes('transmission') || n.includes('gearbox') || n.includes('clutch')) return 'transmission';
    if (n.includes('suspension') || n.includes('brake')) return 'transmission';
    // Engine group
    if (n.includes('engine') || n.includes('exhaust') || n.includes('oil')) return 'engine';
    if (n.includes('battery') || n.includes('electrical')) return 'engine';
    if (n.includes('health') && n.includes('diagnosis')) return 'engine';
    if (n.includes('obd') || n.includes('scanner')) return 'engine';
    // Exterior group
    if (n.includes('exterior') || n.includes('bumper') || n.includes('fender')) return 'exterior';
    if (n.includes('headlight') || n.includes('taillight') || n.includes('indictor') || n.includes('indicator')) return 'exterior';
    if (n.includes('hood') || n.includes('trunk') || n.includes('windshield') || n.includes('glass')) return 'exterior';
    if (n.includes('door panel')) return 'exterior';
    if (n.includes('tyre') || n.includes('tire') || n.includes('wheel')) return 'exterior';
    if (n.includes('physical') && n.includes('inspection')) return 'exterior';
    // Interior group
    if (n.includes('interior') || n.includes('infotainment')) return 'interior';
    if (n.includes('air condition') || n.includes('ac ') || n.includes('cooling')) return 'interior';
    if (n.includes('accessories') && n.includes('check')) return 'interior';
    return null; // General Information, Market Research, Loan Processing etc — don't map
  };
  
  // Calculate all 4 conditions + overall rating from category ratings
  // Uses TWO strategies:
  // 1. Match categoryRatings keys against live-progress category names (for user-set slider values)
  // 2. Directly map ALL categoryRatings keys via mapCategoryToGroup (for DB/AI-set values with different naming)
  // 
  // NOTE: "General Information" category is EXCLUDED from overall rating calculation
  const calculateConditionsFromRatings = (categoryRatings, categories) => {
    const groups = { engine: [], exterior: [], interior: [], transmission: [] };
    const allRatings = [];
    const processedKeys = new Set();
    
    // Helper to check if category is "General Information"
    const isGeneralInfo = (name) => (name || '').toLowerCase().includes('general information') || (name || '').toLowerCase().includes('general_information');
    
    // Strategy 1: Match via live-progress category names
    // Include 0-rated categories in allRatings but only add non-zero to condition groups
    // EXCLUDE General Information from overall rating
    categories.forEach(cat => {
      const catName = cat.category_name || '';
      const catKey = catName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      const rating = categoryRatings[catKey];
      if (rating !== undefined && rating !== null) {
        processedKeys.add(catKey);
        // Skip General Information for overall rating calculation
        if (!isGeneralInfo(catName)) {
          allRatings.push(rating);
        }
        // Only add non-zero ratings to condition groups (0 means "not yet rated for that group")
        if (rating > 0) {
          const group = mapCategoryToGroup(catName);
          if (group) groups[group].push(rating);
        }
      }
    });
    
    // Strategy 2: Directly iterate ALL categoryRatings keys and map via mapCategoryToGroup
    // This catches DB keys like "Physical / Manual Inspection" that don't match live-progress names
    Object.entries(categoryRatings).forEach(([key, rating]) => {
      if (processedKeys.has(key)) return; // Already handled by Strategy 1
      if (rating === undefined || rating === null) return;
      // Skip General Information for overall rating calculation
      if (!isGeneralInfo(key)) {
        allRatings.push(rating);
      }
      // Only add non-zero ratings to condition groups
      if (rating > 0) {
        const group = mapCategoryToGroup(key);
        if (group) groups[group].push(rating);
      }
    });
    
    const avg = (arr) => arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null;
    const result = {};
    
    const eAvg = avg(groups.engine);
    const exAvg = avg(groups.exterior);
    const iAvg = avg(groups.interior);
    const tAvg = avg(groups.transmission);
    
    if (eAvg !== null) result.engine_condition = ratingToCondition(eAvg);
    if (exAvg !== null) result.exterior_condition = ratingToCondition(exAvg);
    if (iAvg !== null) result.interior_condition = ratingToCondition(iAvg);
    if (tAvg !== null) result.transmission_condition = ratingToCondition(tAvg);
    
    // Overall rating: plain average of all category ratings (EXCLUDING General Information)
    if (allRatings.length > 0) {
      result.overall_rating = Math.round((allRatings.reduce((a, b) => a + b, 0) / allRatings.length) * 10) / 10;
    }
    
    return result;
  };
  
  // Update a single field - with auto-calculation for category ratings
  const updateField = (field, value) => {
    // Track ALL manual field edits (per-field protection)
    markFieldAsEdited(field);
    
    // Special handling for category_ratings - track individual category edits
    if (field === 'category_ratings' && typeof value === 'object') {
      Object.keys(value).forEach(categoryKey => {
        markFieldAsEdited(`category_ratings.${categoryKey}`);
      });
    }
    
    setEditData(prev => {
      const newData = { ...prev, [field]: value };
      
      console.log(`[updateField] Field: ${field}, Value: ${value}, locked: ${prev.overall_rating_locked}`);
      
      // LOCK LOGIC: If user manually edits overall_rating, lock it and track history
      if (field === 'overall_rating') {
        newData.overall_rating_locked = true;
        
        // Track manual change in history
        if (prev.overall_rating !== value) {
          const changeEntry = {
            timestamp: new Date().toISOString(),
            type: 'manual_override',
            old_value: prev.overall_rating,
            new_value: value,
            changed_by: user?.name || user?.email || 'Unknown',
            reason: 'Manual override by user'
          };
          newData.overall_rating_history = [...(prev.overall_rating_history || []), changeEntry];
        }
        
        console.log(`[updateField] Overall rating manually set to ${value}, LOCKED`);
        return newData;
      }
      
      // Auto-update condition ratings AND overall_rating when category_ratings change
      if (field === 'category_ratings' && typeof value === 'object') {
        const categories = liveProgressData?.categories || [];
        const conditionUpdates = calculateConditionsFromRatings(value, categories);
        Object.assign(newData, conditionUpdates);
        
        // RECALCULATE LOGIC: Only recalculate if overall_rating is NOT locked
        if (!prev.overall_rating_locked) {
          const allRatings = [];
          const ratingDetails = [];
          
          // Get the valid Q&A category IDs from liveProgressData
          const validCategoryIds = new Set(categories.map(cat => {
            const catId = cat.category_id || cat.id || '';
            // Normalize: remove trailing underscores and convert to lowercase
            return catId.toLowerCase().replace(/_+$/, '').replace(/[^a-z0-9]+/g, '_');
          }));
          
          // Get ratings ONLY from Q&A inspection categories (not package/service categories)
          // EXCLUDE "general_information" from the calculation
          Object.entries(value).forEach(([k, v]) => {
            // Skip General Information category
            if (k.toLowerCase().includes('general_information') || k.toLowerCase().includes('general information')) {
              console.log(`[updateField] Skipping "${k}" from overall rating calculation (General Info)`);
              return;
            }
            
            // Normalize the key for comparison
            const normalizedKey = k.toLowerCase().replace(/_+$/, '').replace(/[^a-z0-9]+/g, '_');
            
            // Only include if it's a valid Q&A category from the inspection template
            const isValidQACategory = validCategoryIds.has(normalizedKey) || 
              categories.some(cat => {
                const catId = (cat.category_id || cat.id || '').toLowerCase().replace(/_+$/, '').replace(/[^a-z0-9]+/g, '_');
                const catName = (cat.category_name || cat.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_');
                return catId === normalizedKey || catName === normalizedKey || 
                       normalizedKey.includes(catId) || catId.includes(normalizedKey);
              });
            
            if (!isValidQACategory) {
              console.log(`[updateField] Skipping "${k}" from overall rating calculation (not a Q&A category)`);
              return;
            }
            
            const rating = typeof v === 'object' && v !== null ? (v.rating ?? v) : v;
            if (rating !== undefined && rating !== null && rating > 0) {
              allRatings.push(rating);
              ratingDetails.push({ category: k, rating });
            }
          });
          
          // Calculate plain simple average of Q&A category ratings only
          if (allRatings.length > 0) {
            const sum = allRatings.reduce((a, b) => a + b, 0);
            const avg = sum / allRatings.length;
            const newRating = Math.round(avg * 10) / 10;
            
            // Track the change for audit log
            if (prev.overall_rating !== newRating) {
              // Find which specific category ratings changed
              const prevCategoryRatings = prev.category_ratings || {};
              const changedCategories = [];
              
              Object.entries(value).forEach(([k, v]) => {
                const newRating = typeof v === 'object' && v !== null ? (v.rating ?? v) : v;
                const oldRating = prevCategoryRatings[k];
                const oldRatingValue = typeof oldRating === 'object' && oldRating !== null ? (oldRating.rating ?? oldRating) : oldRating;
                
                if (oldRatingValue !== undefined && oldRatingValue !== newRating) {
                  // Find the display name for this category
                  const category = categories.find(cat => {
                    const catId = (cat.category_id || cat.id || '').toLowerCase().replace(/_+$/, '').replace(/[^a-z0-9]+/g, '_');
                    const normalizedKey = k.toLowerCase().replace(/_+$/, '').replace(/[^a-z0-9]+/g, '_');
                    return catId === normalizedKey || normalizedKey.includes(catId) || catId.includes(normalizedKey);
                  });
                  const displayName = category?.category_name || category?.name || k.replace(/_/g, ' ');
                  
                  changedCategories.push({
                    category: k,
                    displayName: displayName,
                    oldRating: oldRatingValue || 0,
                    newRating: newRating
                  });
                }
              });
              
              const changeEntry = {
                timestamp: new Date().toISOString(),
                type: 'auto_recalculation',
                old_value: prev.overall_rating,
                new_value: newRating,
                changed_by: user?.name || user?.email || 'System',
                reason: changedCategories.length > 0 
                  ? `${changedCategories.map(c => `"${c.displayName}" changed from ${c.oldRating} to ${c.newRating}`).join(', ')}`
                  : 'Category ratings updated',
                triggered_by: changedCategories,
                calculation: {
                  categories: ratingDetails,
                  sum: sum,
                  count: allRatings.length,
                  average: newRating
                }
              };
              newData.overall_rating_history = [...(prev.overall_rating_history || []), changeEntry];
            }
            
            newData.overall_rating = newRating;
            console.log(`[updateField] Auto-recalculated overall_rating: ${newRating} (avg of ${allRatings.length} Q&A categories, excl. General Info)`);
          }
        } else {
          console.log(`[updateField] Overall rating is LOCKED (${prev.overall_rating}), not recalculating`);
        }
        
        // Also try direct key mapping for conditions if liveProgressData isn't loaded yet
        if (categories.length === 0) {
          const groups = { engine: [], exterior: [], interior: [], transmission: [] };
          Object.entries(value).forEach(([k, v]) => {
            if (v > 0) {
              const group = mapCategoryToGroup(k);
              if (group) groups[group].push(v);
            }
          });
          const avg = (arr) => arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null;
          const eA = avg(groups.engine); if (eA !== null) newData.engine_condition = ratingToCondition(eA);
          const exA = avg(groups.exterior); if (exA !== null) newData.exterior_condition = ratingToCondition(exA);
          const iA = avg(groups.interior); if (iA !== null) newData.interior_condition = ratingToCondition(iA);
          const tA = avg(groups.transmission); if (tA !== null) newData.transmission_condition = ratingToCondition(tA);
        }
      }
      
      return newData;
    });
  };
  
  // Add a repair item
  const addRepair = () => {
    markFieldAsEdited('repairs'); // Track repairs as manually edited
    setEditData(prev => ({
      ...prev,
      repairs: [...prev.repairs, { 
        item: '', 
        type: 'MINOR', 
        action: 'repair',
        price: 0,
        labor: 0,
        estimated_cost: 0,
        part_id: null,
        category: '',
        part_number: '',
        price_source: 'manual',
        // Q&A linking fields for creating repair rules
        qa_category: '',
        question_id: '',
        answer_trigger: ''
      }]
    }));
  };
  
  // Update a repair item
  const updateRepair = (index, updatedRepair) => {
    markFieldAsEdited('repairs'); // Track repairs as manually edited
    markFieldAsEdited(`repairs.${index}`); // Track specific repair index
    setEditData(prev => ({
      ...prev,
      repairs: prev.repairs.map((r, i) => i === index ? updatedRepair : r)
    }));
  };
  
  // Remove a repair item
  const removeRepair = (index) => {
    markFieldAsEdited('repairs'); // Track repairs as manually edited
    setEditData(prev => ({
      ...prev,
      repairs: prev.repairs.filter((_, i) => i !== index)
    }));
  };
  
  // Save repair part/pricing to master data (with duplicate checking and rule creation)
  const saveRepairToMaster = async (repair, index) => {
    try {
      const carTypeKey = (inspection?.car_type || 'sedan').toLowerCase();
      const vehicleBrand = inspection?.vehicle_make || '';
      let partId = repair.part_id;
      let partData = repair.part_data;
      
      // Step 1: Handle Part (Check for duplicates, create/update)
      if (!partId && repair.item) {
        // Check if part already exists by name (prevent duplicates)
        const normalizedName = repair.item.toLowerCase().trim();
        const existingPart = repairParts.find(p => 
          p.name?.toLowerCase().trim() === normalizedName
        );
        
        if (existingPart) {
          // Part exists - use it and update pricing
          partId = existingPart.id;
          partData = existingPart;
          toast.info(`Part "${repair.item}" already exists. Updating pricing.`);
        }
      }
      
      if (partId) {
        // Existing part - update pricing
        const existingPart = partData || repairParts.find(p => p.id === partId);
        if (!existingPart) {
          toast.error('Part not found in master data');
          return;
        }
        
        // Build updated part data with new pricing
        const updatedPart = { ...existingPart };
        
        if (vehicleBrand) {
          // Update or add brand-specific override
          const brandOverrides = updatedPart.brand_overrides || [];
          const existingOverrideIdx = brandOverrides.findIndex(
            bo => bo.brand?.toLowerCase() === vehicleBrand.toLowerCase()
          );
          
          const newPricing = {
            repair_price: repair.price || 0,
            replace_price: repair.action === 'replace' ? repair.price : (updatedPart[carTypeKey]?.replace_price || 0),
            repair_labor: repair.labor || 0,
            replace_labor: repair.action === 'replace' ? repair.labor : (updatedPart[carTypeKey]?.replace_labor || 0)
          };
          
          if (existingOverrideIdx >= 0) {
            brandOverrides[existingOverrideIdx] = {
              ...brandOverrides[existingOverrideIdx],
              brand: vehicleBrand,
              [carTypeKey]: newPricing
            };
          } else {
            brandOverrides.push({
              brand: vehicleBrand,
              [carTypeKey]: newPricing
            });
          }
          updatedPart.brand_overrides = brandOverrides;
        } else {
          // Update default pricing for car type
          updatedPart[carTypeKey] = {
            repair_price: repair.price || 0,
            replace_price: repair.action === 'replace' ? repair.price : (updatedPart[carTypeKey]?.replace_price || 0),
            repair_labor: repair.labor || 0,
            replace_labor: repair.action === 'replace' ? repair.labor : (updatedPart[carTypeKey]?.replace_labor || 0)
          };
        }
        
        // Call API to update part
        await repairsApi.updatePart(partId, updatedPart);
        toast.success(`Pricing for "${repair.item}" saved to master`);
        partData = updatedPart;
        
      } else if (repair.item) {
        // New part - create it in master
        const newPart = {
          name: repair.item,
          category: repair.category || 'Uncategorized',
          description: `Added from inspection repair`,
          part_number: repair.part_number || '',
          hatchback: {
            repair_price: carTypeKey === 'hatchback' ? (repair.price || 0) : 0,
            replace_price: 0,
            repair_labor: carTypeKey === 'hatchback' ? (repair.labor || 0) : 0,
            replace_labor: 0
          },
          sedan: {
            repair_price: carTypeKey === 'sedan' ? (repair.price || 0) : 0,
            replace_price: 0,
            repair_labor: carTypeKey === 'sedan' ? (repair.labor || 0) : 0,
            replace_labor: 0
          },
          suv: {
            repair_price: carTypeKey === 'suv' ? (repair.price || 0) : 0,
            replace_price: 0,
            repair_labor: carTypeKey === 'suv' ? (repair.labor || 0) : 0,
            replace_labor: 0
          },
          brand_overrides: [],
          is_active: true
        };
        
        // If we have a brand, add brand-specific pricing
        if (vehicleBrand) {
          newPart.brand_overrides = [{
            brand: vehicleBrand,
            [carTypeKey]: {
              repair_price: repair.price || 0,
              replace_price: 0,
              repair_labor: repair.labor || 0,
              replace_labor: 0
            }
          }];
        }
        
        // Call API to create part
        const result = await repairsApi.createPart(newPart);
        const createdPart = result.data;
        partId = createdPart.id;
        partData = createdPart;
        toast.success(`New part "${repair.item}" added to master`);
      }
      
      // Step 2: Handle Repair Rule (if Q&A linking is provided)
      if (partId && repair.question_id && repair.answer_trigger) {
        try {
          // Check if rule already exists for this question + part combination
          const rulesRes = await repairsApi.getRules({ question_id: repair.question_id });
          const existingRules = rulesRes.data || [];
          const existingRule = existingRules.find(r => r.part_id === partId);
          
          // Build rule data
          const ruleData = {
            question_id: repair.question_id,
            part_id: partId,
            conditions: [{
              condition: {
                operator: 'contains',
                value: String(repair.answer_trigger || '').toLowerCase()
              },
              action: {
                action_type: repair.action || 'repair',
                priority: repair.type === 'CRITICAL' ? 'critical' : repair.type === 'MAJOR' ? 'high' : 'normal',
                notes: ''
              }
            }],
            is_active: true
          };
          
          if (existingRule) {
            // Update existing rule
            await repairsApi.updateRule(existingRule.id, ruleData);
            toast.success('Repair rule updated');
          } else {
            // Create new rule
            await repairsApi.createRule(ruleData);
            toast.success('Repair rule created');
          }
        } catch (ruleErr) {
          console.error('Failed to save repair rule:', ruleErr);
          toast.warning(`Part saved, but rule creation failed: ${ruleErr.response?.data?.detail || ruleErr.message}`);
        }
      }
      
      // Step 3: Update local state
      updateRepair(index, {
        ...repair,
        part_id: partId,
        price_source: vehicleBrand ? `brand:${vehicleBrand}` : `carType:${carTypeKey}`,
        part_data: partData
      });
      
      // Refresh parts list
      const partsRes = await repairsApi.getParts();
      setRepairParts(partsRes.data || []);
      
    } catch (err) {
      console.error('Failed to save to master:', err);
      toast.error(err.response?.data?.detail || 'Failed to save to master data');
    }
  };
  
  // Calculate total repair costs
  const calculateRepairTotals = () => {
    const total = editData.repairs.reduce((sum, r) => sum + (r.estimated_cost || 0), 0);
    // Minor repairs: +/- 10%, Major: +/- 20%
    const hasMinor = editData.repairs.some(r => r.type === 'MINOR');
    const hasMajor = editData.repairs.some(r => r.type === 'MAJOR' || r.type === 'CRITICAL');
    const variance = hasMajor ? 0.2 : hasMinor ? 0.1 : 0;
    return {
      min: Math.round(total * (1 - variance)),
      max: Math.round(total * (1 + variance))
    };
  };
  
  // Fetch publish history when Report tab is active
  useEffect(() => {
    if (activeTab === 'report' && inspection?.id) {
      fetchPublishHistory();
    }
  }, [activeTab, inspection?.id]);
  
  // Request OBD Rescan
  const requestObdRescan = async () => {
    if (!inspection?.id) return;
    setRequestingRescan(true);
    try {
      await inspectionsApi.requestObdRescan(inspection.id);
      toast.success('OBD rescan requested. Mechanic will be notified.');
      onRefresh(inspection.id);
    } catch (err) {
      toast.error('Failed to request OBD rescan');
      console.error(err);
    } finally {
      setRequestingRescan(false);
    }
  };
  
  // Get market_price_research from liveProgressData if available (fresher), otherwise from inspection prop
  const marketPriceResearch = useMemo(() => {
    return liveProgressData?.market_price_research || inspection?.market_price_research || null;
  }, [liveProgressData?.market_price_research, inspection?.market_price_research]);
  
  // Get DTC codes from OBD data
  const dtcCodes = useMemo(() => {
    const obdData = liveProgressData?.obd_scan?.data;
    if (!obdData) return [];
    
    // Parse DTC codes from various possible formats
    if (Array.isArray(obdData.dtc_codes)) return obdData.dtc_codes;
    if (obdData.dtcCodes && Array.isArray(obdData.dtcCodes)) return obdData.dtcCodes;
    if (obdData.trouble_codes && Array.isArray(obdData.trouble_codes)) return obdData.trouble_codes;
    
    // Format 1: raw_codes array (from mechanic app OBD scanner)
    // This is the most detailed format with all code info
    if (Array.isArray(obdData.raw_codes) && obdData.raw_codes.length > 0) {
      return obdData.raw_codes.map(c => ({
        code: c.code,
        description: c.description || '',
        system: c.categoryName || c.category || '',
        ecuSource: c.ecuSource || '',
        status: c.status || '',
        // v1.60+ / v1.61+ category & demotion fields (preserve so OBDTab can
        // filter and render DtcStatusBadge). Older inspections won't have
        // these — the UI falls back to parsing `status` text.
        statusCategory: c.statusCategory || null,
        confidenceTier: c.confidenceTier || null,
        demotedReason: c.demotedReason || null,
        isManufacturerSpecific: c.isManufacturerSpecific || false,
        possible_causes: c.possible_causes || c.possibleCauses || [],
        symptoms: c.symptoms || [],
        recommended_solutions: c.recommended_solutions || c.recommendedSolutions || c.solutions || [],
      }));
    }
    
    // Format 2: categories array with nested codes
    // { categories: [{ name: 'P', codes: [{ code, description, status }] }] }
    if (Array.isArray(obdData.categories) && obdData.categories.length > 0) {
      const codes = [];
      obdData.categories.forEach(category => {
        if (Array.isArray(category.codes)) {
          category.codes.forEach(c => {
            codes.push({
              code: c.code,
              description: c.description || '',
              system: category.name === 'P' ? 'Powertrain' : 
                      category.name === 'U' ? 'Network' :
                      category.name === 'B' ? 'Body' :
                      category.name === 'C' ? 'Chassis' : category.name,
              status: c.status || '',
              possible_causes: c.possible_causes || [],
              symptoms: c.symptoms || [],
              recommended_solutions: c.recommended_solutions || [],
            });
          });
        }
      });
      return codes;
    }
    
    // Format 3: diagnostic_trouble_codes object (legacy)
    const codes = [];
    if (obdData.diagnostic_trouble_codes) {
      Object.entries(obdData.diagnostic_trouble_codes).forEach(([system, systemCodes]) => {
        if (Array.isArray(systemCodes)) {
          codes.push(...systemCodes.map(c => ({ code: c.code || c, description: c.description || '', system })));
        }
      });
    }
    return codes;
  }, [liveProgressData?.obd_scan?.data]);
  
  // Load existing DTC analysis from OBD data and auto-select all codes
  // CRITICAL: Detect stale analysis — if the analysis has fewer codes than
  // the current DTC scan, a rescan happened and we need to re-analyze.
  useEffect(() => {
    const existingAnalysis = liveProgressData?.obd_scan?.data?.dtc_analysis;
    if (existingAnalysis) {
      const analyzedCount = (existingAnalysis.analyzed_codes || []).length;
      const actualCount = dtcCodes.length;
      
      if (actualCount > 0 && analyzedCount > 0 && analyzedCount < actualCount) {
        // Stale analysis: fewer analyzed codes than actual DTC codes
        // Clear it so auto-analyze triggers with the full set
        console.log(`[OBD] Stale analysis detected: ${analyzedCount} analyzed vs ${actualCount} actual codes. Clearing.`);
        setDtcAnalysis(null);
        return;
      }
      
      setDtcAnalysis(existingAnalysis);
      // Don't auto-select-all here — HierarchicalFilter controls selection
      // via onFilteredCodesChange callback. Only set as fallback if no hierarchy.
    } else {
      // No analysis exists (cleared by rescan) — reset state
      setDtcAnalysis(null);
    }
  }, [liveProgressData?.obd_scan?.data?.dtc_analysis, dtcCodes.length]);
  
  // Auto-run AI analysis when OBD-2 tab is opened and codes exist but no analysis
  useEffect(() => {
    if (activeTab === 'obd' && dtcCodes.length > 0 && !dtcAnalysis && !analyzingDTC) {
      analyzeDtcCodes();
    }
  }, [activeTab, dtcCodes.length, dtcAnalysis, analyzingDTC]);
  
  // Analyze DTC codes with AI
  const analyzeDtcCodes = async () => {
    if (!inspection?.id || !dtcCodes.length || analyzingDTC) return;
    
    // Clear old analysis immediately — this ensures stale severity counts
    // don't persist while re-analysis is in progress
    setDtcAnalysis(null);
    setAnalyzingDTC(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/inspections/${inspection.id}/analyze-dtc`,
        { dtc_codes: dtcCodes }
      );
      
      if (response.data.success) {
        setDtcAnalysis(response.data);
        // Don't auto-select-all — HierarchicalFilter controls selection
      }
    } catch (err) {
      console.error('DTC analysis failed:', err);
      toast.error('Failed to analyze DTC codes');
    } finally {
      setAnalyzingDTC(false);
    }
  };
  
  // Toggle selection of codes by severity
  const toggleSeveritySelection = (severity) => {
    if (!dtcAnalysis?.analyzed_codes) return;
    
    const codesOfSeverity = dtcAnalysis.analyzed_codes
      .filter(c => severity === 'all' || c.severity === severity)
      .map(c => c.code);
    
    // Check if all codes of this severity are currently selected
    const allSelected = codesOfSeverity.every(code => selectedDtcCodes.has(code));
    
    setSelectedDtcCodes(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all codes of this severity
        codesOfSeverity.forEach(code => newSet.delete(code));
      } else {
        // Select all codes of this severity
        codesOfSeverity.forEach(code => newSet.add(code));
      }
      return newSet;
    });
  };
  
  // Check if all codes of a severity are selected
  const isSeverityFullySelected = (severity) => {
    if (!dtcAnalysis?.analyzed_codes) return false;
    
    const codesOfSeverity = dtcAnalysis.analyzed_codes
      .filter(c => severity === 'all' || c.severity === severity)
      .map(c => c.code);
    
    return codesOfSeverity.length > 0 && codesOfSeverity.every(code => selectedDtcCodes.has(code));
  };
  
  // Check if some (but not all) codes of a severity are selected
  const isSeverityPartiallySelected = (severity) => {
    if (!dtcAnalysis?.analyzed_codes) return false;
    
    const codesOfSeverity = dtcAnalysis.analyzed_codes
      .filter(c => severity === 'all' || c.severity === severity)
      .map(c => c.code);
    
    const selectedCount = codesOfSeverity.filter(code => selectedDtcCodes.has(code)).length;
    return selectedCount > 0 && selectedCount < codesOfSeverity.length;
  };
  
  // Filter DTC codes based on severity selection AND status filter
  const STATUS_LABEL_MAP = { 'Stored': 'History', 'stored': 'History', 'Pending': 'Pending', 'pending': 'Pending', 'Active': 'Active', 'active': 'Active', 'Confirmed': 'Active', 'confirmed': 'Active' };
  const getStatusLabel = (s) => STATUS_LABEL_MAP[s] || s || 'History';

  // Callback: HierarchicalFilter reports its computed filter output.
  // This is the SINGLE source of truth for which codes pass the 3-level cascade.
  const handleHierarchyFilterChange = useCallback((filteredSet) => {
    setHierarchyFilteredCodes(filteredSet);
    // Auto-sync report selection to match hierarchy output
    setSelectedDtcCodes(filteredSet);
  }, []);

  const filteredDtcCodes = useMemo(() => {
    if (!dtcAnalysis?.analyzed_codes) return dtcCodes;
    
    const analysisMap = {};
    dtcAnalysis.analyzed_codes.forEach(c => {
      analysisMap[c.code] = c;
    });
    
    // Merge original DTC data with analysis
    let merged = dtcCodes.map(dtc => ({
      ...dtc,
      analysis: analysisMap[dtc.code] || null
    }));
    
    // Filter by hierarchyFilteredCodes (set by HierarchicalFilter cascade)
    // This ensures the grouped DTC list only shows codes that passed
    // ALL 3 filter levels: confidence → status → severity
    if (hierarchyFilteredCodes && hierarchyFilteredCodes.size > 0) {
      merged = merged.filter(d => hierarchyFilteredCodes.has(d.code));
    }
    
    return merged;
  }, [dtcCodes, dtcAnalysis, hierarchyFilteredCodes]);
  
  // Get component full name - must be defined before groupedDtcCodes
  const getComponentFullName = (code) => {
    const names = {
      'ECM': 'Engine Control Module',
      'TCM': 'Transmission Control Module',
      'ABS': 'Anti-lock Braking System',
      'BCM': 'Body Control Module',
      'HVAC': 'Climate Control System',
      'SRS': 'Airbag/Safety System',
      'PCM': 'Powertrain Control Module',
      'EPS': 'Electric Power Steering',
      'FUEL': 'Fuel System',
      'EMISSION': 'Emissions System',
      'ELECTRICAL': 'Electrical System',
      'NETWORK': 'CAN Bus/Network',
      'UNKNOWN': 'Uncategorized'
    };
    return names[code] || code;
  };
  
  // Group DTC codes by component
  const groupedDtcCodes = useMemo(() => {
    if (!dtcAnalysis?.analyzed_codes) return null;
    
    const groups = {};
    const analysisMap = {};
    dtcAnalysis.analyzed_codes.forEach(c => {
      analysisMap[c.code] = c;
    });
    
    filteredDtcCodes.forEach(dtc => {
      const analysis = analysisMap[dtc.code];
      const component = analysis?.component || 'UNKNOWN';
      
      if (!groups[component]) {
        groups[component] = {
          name: component,
          fullName: getComponentFullName(component),
          codes: [],
          criticalCount: 0,
          importantCount: 0,
          nonImportantCount: 0
        };
      }
      
      groups[component].codes.push({ ...dtc, analysis });
      
      if (analysis?.severity === 'CRITICAL') groups[component].criticalCount++;
      else if (analysis?.severity === 'IMPORTANT') groups[component].importantCount++;
      else groups[component].nonImportantCount++;
    });
    
    // Sort by critical count descending
    return Object.values(groups).sort((a, b) => 
      (b.criticalCount * 100 + b.importantCount * 10) - (a.criticalCount * 100 + a.importantCount * 10)
    );
  }, [filteredDtcCodes, dtcAnalysis]);
  
  // Toggle DTC code selection for report
  const toggleDtcSelection = (code) => {
    setSelectedDtcCodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };
  
  // Select/Deselect all filtered codes
  const selectAllFilteredCodes = (select) => {
    if (select) {
      const codes = filteredDtcCodes.map(d => d.code);
      setSelectedDtcCodes(new Set(codes));
    } else {
      setSelectedDtcCodes(new Set());
    }
  };
  
  // Save DTC selection to report (includes status filter)
  const saveDtcSelection = async () => {
    if (!inspection?.id) return;
    
    setSavingDtcSelection(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/inspections/${inspection.id}/dtc-report-selection`,
        {
          selected_codes: Array.from(selectedDtcCodes),
          filter_type: dtcFilterType,
          status_filter: statusFilter,
        }
      );
      toast.success(`${selectedDtcCodes.size} DTC codes saved for report`);
    } catch (err) {
      console.error('Failed to save DTC selection:', err);
      toast.error('Failed to save DTC selection');
    } finally {
      setSavingDtcSelection(false);
    }
  };
  
  // ========== REPAIR SELECTION & COST EDITING ==========
  
  // Generate unique key for a repair item (unique by part_id — one part can only be repaired OR replaced)
  const getRepairKey = (repair) => `${repair.part_id}`;
  
  // Toggle repair selection for report
  const toggleRepairSelection = (repair) => {
    const key = getRepairKey(repair);
    setSelectedRepairs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };
  
  // Update repair cost override (local only)
  const updateRepairCostOverride = (repair, field, value) => {
    const key = getRepairKey(repair);
    setRepairCostOverrides(prev => {
      const existing = prev[key] || { price: repair.price, labor: repair.labor };
      const updated = { ...existing, [field]: parseFloat(value) || 0 };
      updated.cost = (updated.price || 0) + (updated.labor || 0);
      return { ...prev, [key]: updated };
    });
  };
  
  // Get effective cost for a repair (with override if exists)
  const getEffectiveRepairCost = (repair) => {
    const key = getRepairKey(repair);
    const override = repairCostOverrides[key];
    if (override) {
      return {
        price: override.price ?? repair.price,
        labor: override.labor ?? repair.labor,
        cost: override.cost ?? repair.cost,
        isOverridden: true
      };
    }
    return { price: repair.price, labor: repair.labor, cost: repair.cost, isOverridden: false };
  };
  
  // Save repair selection to inspection
  const saveRepairSelection = async () => {
    if (!inspection?.id) return;
    
    setSavingRepairSelection(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/inspections/${inspection.id}/repair-report-selection`,
        {
          selected_repairs: Array.from(selectedRepairs),
          cost_overrides: repairCostOverrides
        }
      );
      toast.success(`${selectedRepairs.size} repairs saved for report`);
    } catch (err) {
      console.error('Failed to save repair selection:', err);
      toast.error('Failed to save repair selection');
    } finally {
      setSavingRepairSelection(false);
    }
  };
  
  // Update master data (repair_parts collection) with new cost
  const updateMasterRepairCost = async (repair) => {
    const key = getRepairKey(repair);
    const override = repairCostOverrides[key];
    if (!override || !repair.part_id) {
      toast.error('No cost changes to update');
      return;
    }
    
    setUpdatingMasterCost(repair.part_id);
    try {
      const carType = (inspection?.car_type || 'sedan').toLowerCase();
      
      // Update the repair part's pricing for this car type
      await repairsApi.updatePart(repair.part_id, {
        [carType]: {
          repair_price: override.price,
          repair_labor: override.labor
        }
      });
      
      // Refresh parts data
      const partsRes = await repairsApi.getParts();
      setRepairParts(partsRes.data || []);
      
      toast.success(`Master pricing updated for ${repair.part_name} (${carType})`);
    } catch (err) {
      console.error('Failed to update master cost:', err);
      toast.error('Failed to update master pricing');
    } finally {
      setUpdatingMasterCost(null);
    }
  };
  
  // ========== END REPAIR SELECTION ==========
  
  // Get severity badge color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-100 text-red-700 border-red-200';
      case 'IMPORTANT': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'NON_IMPORTANT': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  
  // Calculate repairs based on inspection answers and rules
  const calculatedRepairCosts = useMemo(() => {
    if (!repairRules.length || !repairParts.length) return [];
    
    const repairs = [];
    const categories = liveProgressData?.categories || [];
    const carType = (inspection?.car_type || 'sedan').toLowerCase();
    const brand = inspection?.car_make || inspection?.vehicle_make || '';
    
    // Get inspection_answers from the inspection document (keyed by question ID)
    const inspectionAnswers = inspection?.inspection_answers || {};
    
    // Build a map of question IDs to question text from inspectionQuestions
    const questionTextMap = {};
    inspectionQuestions.forEach(q => {
      if (q.id && q.question) {
        questionTextMap[q.id] = q.question;
      }
    });
    
    // Helper: Get price for a part based on car type and brand
    const getPartPrice = (part, action = 'repair') => {
      if (!part) return { price: 0, labor: 0, total: 0 };
      
      // Check for brand-specific override first
      const brandOverride = part.brand_overrides?.find(
        bo => bo.brand?.toLowerCase() === brand?.toLowerCase()
      );
      
      let pricing;
      if (brandOverride && brandOverride[carType]) {
        pricing = brandOverride[carType];
      } else if (part[carType]) {
        pricing = part[carType];
      } else {
        pricing = part.sedan || {};
      }
      
      // Handle different action types
      let price = 0;
      let labor = 0;
      
      switch ((action || 'repair').toLowerCase()) {
        case 'repair':
          // Part cost only (repair price)
          price = pricing.repair_price || 0;
          labor = 0;
          break;
        case 'labor':
          // Labor cost only
          price = 0;
          labor = pricing.repair_labor || 0;
          break;
        case 'both':
          // Both part and labor
          price = pricing.repair_price || 0;
          labor = pricing.repair_labor || 0;
          break;
        case 'replace':
          // Replace costs (for backward compatibility)
          price = pricing.replace_price || 0;
          labor = pricing.replace_labor || 0;
          break;
        case 'inspect_further':
          // No cost, just flag for inspection
          price = 0;
          labor = 0;
          break;
        default:
          price = pricing.repair_price || 0;
          labor = pricing.repair_labor || 0;
      }
      
      return { price, labor, total: price + labor };
    };
    
    // Helper: Extract answer value from various formats
    const extractAnswerValue = (answerData) => {
      if (!answerData) return '';
      if (typeof answerData === 'string') {
        if (answerData.startsWith('media_ref:')) return '';
        return answerData;
      }
      if (typeof answerData === 'object') {
        const answer = answerData.answer || answerData;
        if (typeof answer === 'object') {
          return answer.selection || answer.value || '';
        }
        if (typeof answer === 'string' && answer.startsWith('media_ref:')) return '';
        return String(answer);
      }
      return String(answerData);
    };
    
    // Helper: Check if answer matches rule condition
    const checkCondition = (answer, rule) => {
      if (answer == null) return false;
      // Extract string value from answer (might be dict like {selection: 'No'})
      let answerStr;
      if (typeof answer === 'object') {
        answerStr = answer.selection || answer.value || answer.answer || '';
        if (typeof answerStr === 'object') answerStr = answerStr.selection || answerStr.value || '';
      } else {
        answerStr = String(answer);
      }
      if (!answerStr) return false;
      const answerLower = String(answerStr).toLowerCase().trim();
      const conditionValue = String(rule.condition_value || '').toLowerCase().trim();
      const conditionType = rule.condition_type || 'CONTAINS';
      
      switch (conditionType) {
        case 'EQUALS':
          return answerLower === conditionValue;
        case 'CONTAINS':
          return answerLower.includes(conditionValue) || conditionValue.includes(answerLower);
        case 'NOT_EQUALS':
          return answerLower !== conditionValue;
        case 'GREATER_THAN':
          return parseFloat(answerLower) > parseFloat(conditionValue);
        case 'LESS_THAN':
          return parseFloat(answerLower) < parseFloat(conditionValue);
        default:
          return answerLower.includes(conditionValue) || answerLower === conditionValue;
      }
    };
    
    // Helper: Normalize text for comparison
    const normalizeText = (text) => {
      return (text || '').toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };
    
    // Helper: Check if two questions are similar (fuzzy match)
    const questionsMatch = (text1, text2) => {
      const norm1 = normalizeText(text1);
      const norm2 = normalizeText(text2);
      
      if (!norm1 || !norm2) return false;
      
      // Exact match
      if (norm1 === norm2) return true;
      
      // Check significant word overlap
      const words1 = norm1.split(' ').filter(w => w.length > 3);
      const words2 = norm2.split(' ').filter(w => w.length > 3);
      
      if (words1.length === 0 || words2.length === 0) return false;
      
      const matchingWords = words1.filter(w1 => 
        words2.some(w2 => w1.includes(w2) || w2.includes(w1))
      );
      
      // Require at least 2 matching words or 50% overlap
      return matchingWords.length >= 2 || matchingWords.length >= Math.min(words1.length, words2.length) * 0.5;
    };
    
    // Strategy 1: Match using inspection_answers + inspectionQuestions (most reliable)
    Object.entries(inspectionAnswers).forEach(([questionId, answerData]) => {
      const answerValue = extractAnswerValue(answerData);
      
      // Get question text from our map or from the answer data itself
      const questionText = questionTextMap[questionId] || answerData?.question || '';
      
      // Extract sub_answer values (MCQ answers for photo questions - Dent/Scratch severity)
      const subAnswer1 = typeof answerData === 'object' ? answerData?.sub_answer_1 : null; // Dent severity
      const subAnswer2 = typeof answerData === 'object' ? answerData?.sub_answer_2 : null; // Scratch severity
      
      // Skip if no relevant answers
      if (!answerValue && !subAnswer1 && !subAnswer2) return;
      
      // Find rules that match this question
      repairRules.forEach(rule => {
        if (!rule.is_active) return;
        
        let isMatch = false;
        
        // Try exact question ID match
        if (rule.question_id && rule.question_id === questionId) {
          isMatch = true;
        }
        // Try text matching
        else if (rule.question_text && questionText) {
          isMatch = questionsMatch(rule.question_text, questionText);
        }
        
        if (!isMatch) return;
        
        // Determine which answer to check based on rule's sub_answer_type
        let valueToCheck = answerValue;
        let answerLabel = 'Answer';
        
        // Check if this rule targets sub_answer_1 (Dent) or sub_answer_2 (Scratch)
        if (rule.sub_answer_type === 'dent' || rule.sub_answer_type === 'sub_answer_1') {
          valueToCheck = subAnswer1;
          answerLabel = 'Dent';
        } else if (rule.sub_answer_type === 'scratch' || rule.sub_answer_type === 'sub_answer_2') {
          valueToCheck = subAnswer2;
          answerLabel = 'Scratch';
        } else if (!answerValue && subAnswer1) {
          // For photo questions without text answer, check sub_answer_1 (Dent) first
          if (subAnswer1 && subAnswer1 !== 'No Dents' && checkCondition(subAnswer1, rule)) {
            valueToCheck = subAnswer1;
            answerLabel = 'Dent';
          } else if (subAnswer2 && subAnswer2 !== 'No Scratch' && checkCondition(subAnswer2, rule)) {
            valueToCheck = subAnswer2;
            answerLabel = 'Scratch';
          }
        }
        
        // Check if answer matches condition
        if (valueToCheck && checkCondition(valueToCheck, rule)) {
          const part = repairParts.find(p => p.id === rule.part_id);
          if (part) {
            const actionType = rule.action_type || 'REPAIR';
            const priceInfo = getPartPrice(part, actionType.toLowerCase());
            
            // Deduplicate by part_id: one spare part can only be repaired OR replaced, not both.
            // REPLACE takes priority over REPAIR. Among same action, higher priority wins.
            const ACTION_RANK = { REPLACE: 1, REPAIR: 2 };
            const PRIORITY_RANK = { critical: 0, high: 1, normal: 2, medium: 2, low: 3 };
            const getPrio = (p) => typeof p === 'number' ? p : (PRIORITY_RANK[p] ?? 2);
            const existingIdx = repairs.findIndex(r => r.part_id === part.id);
            
            if (existingIdx >= 0 && priceInfo.total > 0) {
              const existing = repairs[existingIdx];
              const newActionRank = ACTION_RANK[actionType.toUpperCase()] || 2;
              const existActionRank = ACTION_RANK[existing.action] || 2;
              // Replace if: new action is more severe, OR same action but higher priority
              if (newActionRank < existActionRank || (newActionRank === existActionRank && getPrio(rule.priority || 'normal') < getPrio(existing.priority))) {
                repairs[existingIdx] = {
                  part_id: part.id,
                  part_name: part.name,
                  part_number: part.part_number,
                  category: part.category,
                  category_name: 'From Q&A',
                  action: actionType.toUpperCase(),
                  question_id: questionId,
                  question: questionText || rule.question_text,
                  answer: `${answerLabel}: ${valueToCheck}`,
                  condition_matched: `${rule.condition_type}: ${rule.condition_value}`,
                  price: priceInfo.price,
                  labor: priceInfo.labor,
                  cost: priceInfo.total,
                  priority: rule.priority || 'normal',
                  rule_id: rule.id
                };
              }
            } else if (existingIdx < 0 && priceInfo.total > 0) {
              repairs.push({
                part_id: part.id,
                part_name: part.name,
                part_number: part.part_number,
                category: part.category,
                category_name: 'From Q&A',
                action: actionType.toUpperCase(),
                question_id: questionId,
                question: questionText || rule.question_text,
                answer: `${answerLabel}: ${valueToCheck}`,
                condition_matched: `${rule.condition_type}: ${rule.condition_value}`,
                price: priceInfo.price,
                labor: priceInfo.labor,
                cost: priceInfo.total,
                priority: rule.priority || 'normal',
                rule_id: rule.id
              });
            }
          }
        }
      });
    });
    
    // Strategy 2: Also check liveProgressData.categories for questions (backup)
    categories.forEach(cat => {
      (cat.questions || []).forEach(q => {
        const answerValue = extractAnswerValue(q.answer);
        const subAnswer1 = q.sub_answer_1; // Dent severity from live-progress API
        const subAnswer2 = q.sub_answer_2; // Scratch severity from live-progress API
        
        // Skip if no relevant answers
        if (!answerValue && !subAnswer1 && !subAnswer2) return;
        
        const qText = q.question_text || q.question || q.text || '';
        
        repairRules.forEach(rule => {
          if (!rule.is_active) return;
          
          let isMatch = false;
          
          // Try exact ID match
          if (rule.question_id && q.question_id && rule.question_id === q.question_id) {
            isMatch = true;
          }
          // Try text matching
          else if (rule.question_text && qText) {
            isMatch = questionsMatch(rule.question_text, qText);
          }
          
          if (!isMatch) return;
          
          // Determine which answer to check based on rule's sub_answer_type
          let valueToCheck = answerValue;
          let answerLabel = 'Answer';
          
          if (rule.sub_answer_type === 'dent' || rule.sub_answer_type === 'sub_answer_1') {
            valueToCheck = subAnswer1;
            answerLabel = 'Dent';
          } else if (rule.sub_answer_type === 'scratch' || rule.sub_answer_type === 'sub_answer_2') {
            valueToCheck = subAnswer2;
            answerLabel = 'Scratch';
          } else if (!answerValue) {
            // For photo questions, check sub_answers
            if (subAnswer1 && subAnswer1 !== 'No Dents' && checkCondition(subAnswer1, rule)) {
              valueToCheck = subAnswer1;
              answerLabel = 'Dent';
            } else if (subAnswer2 && subAnswer2 !== 'No Scratch' && checkCondition(subAnswer2, rule)) {
              valueToCheck = subAnswer2;
              answerLabel = 'Scratch';
            }
          }
          
          if (valueToCheck && checkCondition(valueToCheck, rule)) {
            const part = repairParts.find(p => p.id === rule.part_id);
            if (part) {
              const actionType = rule.action_type || 'REPAIR';
              const priceInfo = getPartPrice(part, actionType.toLowerCase());
              
              // Deduplicate by part_id: one spare part = one entry. REPLACE > REPAIR priority.
              const ACTION_RANK = { REPLACE: 1, REPAIR: 2 };
              const PRIORITY_RANK = { critical: 0, high: 1, normal: 2, medium: 2, low: 3 };
              const getPrio = (p) => typeof p === 'number' ? p : (PRIORITY_RANK[p] ?? 2);
              const existingIdx = repairs.findIndex(r => r.part_id === part.id);
              
              if (existingIdx >= 0 && priceInfo.total > 0) {
                const existing = repairs[existingIdx];
                const newActionRank = ACTION_RANK[actionType.toUpperCase()] || 2;
                const existActionRank = ACTION_RANK[existing.action] || 2;
                if (newActionRank < existActionRank || (newActionRank === existActionRank && getPrio(rule.priority || 'normal') < getPrio(existing.priority))) {
                  repairs[existingIdx] = {
                    part_id: part.id,
                    part_name: part.name,
                    part_number: part.part_number,
                    category: part.category,
                    category_name: cat.category_name || cat.name || 'Unknown',
                    action: actionType.toUpperCase(),
                    question_id: q.question_id || q.id,
                    question: qText || rule.question_text,
                    answer: `${answerLabel}: ${valueToCheck}`,
                    condition_matched: `${rule.condition_type}: ${rule.condition_value}`,
                    price: priceInfo.price,
                    labor: priceInfo.labor,
                    cost: priceInfo.total,
                    priority: rule.priority || 'normal',
                    rule_id: rule.id
                  };
                }
              } else if (existingIdx < 0 && priceInfo.total > 0) {
                repairs.push({
                  part_id: part.id,
                  part_name: part.name,
                  part_number: part.part_number,
                  category: part.category,
                  category_name: cat.category_name || cat.name || 'Unknown',
                  action: actionType.toUpperCase(),
                  question_id: q.question_id || q.id,
                  question: qText || rule.question_text,
                  answer: `${answerLabel}: ${valueToCheck}`,
                  condition_matched: `${rule.condition_type}: ${rule.condition_value}`,
                  price: priceInfo.price,
                  labor: priceInfo.labor,
                  cost: priceInfo.total,
                  priority: rule.priority || 'normal',
                  rule_id: rule.id
                });
              }
            }
          }
        });
      });
    });
    
    // Sort by priority (critical/1 > high/2 > normal/3 > low)
    // Support both numeric (1=high, 2=medium, 3=low) and string priorities
    const getPriorityValue = (priority) => {
      if (typeof priority === 'number') return priority;
      const priorityOrder = { critical: 0, high: 1, normal: 2, low: 3 };
      return priorityOrder[priority] || 2;
    };
    repairs.sort((a, b) => getPriorityValue(a.priority) - getPriorityValue(b.priority));
    
    return repairs;
  }, [repairRules, repairParts, inspectionQuestions, liveProgressData?.categories, inspection]);
  
  // Select/Deselect all repairs
  const selectAllRepairs = (select) => {
    if (select) {
      const keys = calculatedRepairCosts.map(r => getRepairKey(r));
      setSelectedRepairs(new Set(keys));
    } else {
      setSelectedRepairs(new Set());
    }
  };
  
  // Track if we've initialized repairs from saved selection
  const [repairSelectionInitialized, setRepairSelectionInitialized] = useState(false);
  
  // Load saved repair selection from inspection FIRST (this takes priority)
  useEffect(() => {
    if (inspection?.repair_report_selection) {
      const saved = inspection.repair_report_selection;
      if (saved.selected_repairs) {
        // Load saved selection - this might be an empty array if user intentionally deselected all
        setSelectedRepairs(new Set(saved.selected_repairs));
        setRepairSelectionInitialized(true);
        console.log('[REPAIRS] Loaded saved selection:', saved.selected_repairs.length, 'items');
      }
      if (saved.cost_overrides) {
        setRepairCostOverrides(saved.cost_overrides);
      }
    }
  }, [inspection?.id, inspection?.repair_report_selection]);
  
  // Initialize selected repairs when calculatedRepairCosts changes (select all by default)
  // BUT only if no saved selection exists
  useEffect(() => {
    // Don't auto-select if:
    // 1. Already initialized from saved selection
    // 2. User already has some selection
    // 3. There's a saved selection in the inspection (even if empty array)
    if (calculatedRepairCosts.length > 0 && 
        selectedRepairs.size === 0 && 
        !repairSelectionInitialized &&
        !inspection?.repair_report_selection?.selected_repairs) {
      console.log('[REPAIRS] Auto-selecting all repairs (no saved selection)');
      selectAllRepairs(true);
    }
  }, [calculatedRepairCosts.length, repairSelectionInitialized, inspection?.repair_report_selection]);
  
  // Reset initialization flag when inspection changes
  useEffect(() => {
    setRepairSelectionInitialized(false);
  }, [inspection?.id]);
  
  // Component ID to display name mapping (hardcoded for reliability)
  const COMPONENT_DISPLAY_NAMES = {
    'body_panels': 'Body Panels',
    'mechanical': 'Mechanical Parts',
    'electrical': 'Electrical',
    'interior': 'Interior',
    'glass': 'Glass & Mirrors',
    'tyres_wheels': 'Tyres & Wheels',
    'ac_cooling': 'AC & Cooling',
    'exhaust': 'Exhaust System',
    'general_repairs': 'General Repairs',
  };
  
  // Helper to get component display name (uses partCategories if available, fallback to constant)
  const getComponentDisplayName = useCallback((componentId) => {
    // First check partCategories from API
    const fromApi = partCategories.find(cat => cat.id === componentId);
    if (fromApi) return fromApi.name;
    // Fallback to constant mapping
    return COMPONENT_DISPLAY_NAMES[componentId] || componentId || 'General Repairs';
  }, [partCategories]);
  
  // Group repairs by component (from spare part) for accordion display
  const repairsByCategory = useMemo(() => {
    if (!calculatedRepairCosts || calculatedRepairCosts.length === 0) return {};
    
    const groups = {};
    calculatedRepairCosts.forEach(repair => {
      // Use component (part.category) for grouping, get display name
      const componentId = repair.category || 'general_repairs';
      const componentDisplayName = getComponentDisplayName(componentId);
      if (!groups[componentDisplayName]) {
        groups[componentDisplayName] = [];
      }
      groups[componentDisplayName].push(repair);
    });
    
    // Sort components by total cost (highest first)
    return Object.entries(groups)
      .sort((a, b) => {
        const totalA = a[1].reduce((sum, r) => sum + (r.cost || 0), 0);
        const totalB = b[1].reduce((sum, r) => sum + (r.cost || 0), 0);
        return totalB - totalA;
      })
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  }, [calculatedRepairCosts, getComponentDisplayName]);
  
  // State for expanded categories in repairs accordion
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Toggle category expansion
  const toggleCategoryExpansion = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Save all changes
  const saveChanges = async () => {
    if (!inspection?.id) {
      toast.error('No inspection selected');
      return;
    }
    
    // Validate and show missing data before saving
    const missing = validateMissingData();
    setMissingDataItems(missing);
    
    // Show warning if there's missing data but still allow saving
    if (missing.length > 0) {
      const highPriority = missing.filter(m => m.priority === 'high').length;
      if (highPriority > 0) {
        toast.warning(`${highPriority} high priority items are missing. Data will be saved but may be incomplete.`);
      }
      setShowMissingDataAlert(true);
    }
    
    setSaving(true);
    try {
      // Calculate repair totals safely
      const repairs = editData.repairs || [];
      const total = repairs.reduce((sum, r) => sum + (r.estimated_cost || 0), 0);
      const hasMinor = repairs.some(r => r.type === 'MINOR');
      const hasMajor = repairs.some(r => r.type === 'MAJOR' || r.type === 'CRITICAL');
      const variance = hasMajor ? 0.2 : hasMinor ? 0.1 : 0;
      const repairTotals = {
        min: Math.round(total * (1 - variance)),
        max: Math.round(total * (1 + variance))
      };
      
      // Helper to convert empty strings to 0 for numbers, ensure strings are strings
      const sanitize = (val, type = 'string') => {
        if (type === 'number') {
          if (val === '' || val === null || val === undefined) return 0;
          return typeof val === 'number' ? val : parseFloat(val) || 0;
        }
        if (type === 'string') {
          if (val === null || val === undefined) return '';
          return String(val);
        }
        return val;
      };
      
      // Build update payload with proper type conversion
      const updatePayload = {
        overall_rating: sanitize(editData.overall_rating, 'number'),
        overall_rating_locked: editData.overall_rating_locked || false, // Save lock state to DB
        recommended_to_buy: editData.recommended_to_buy || false,
        market_value_min: sanitize(editData.market_value_min, 'number'),
        market_value_max: sanitize(editData.market_value_max, 'number'),
        assessment_summary: sanitize(editData.assessment_summary, 'string'),
        key_highlights: Array.isArray(editData.key_highlights) ? editData.key_highlights : [],
        vehicle_make: sanitize(editData.vehicle_make, 'string'),
        vehicle_model: sanitize(editData.vehicle_model, 'string'),
        vehicle_year: sanitize(editData.vehicle_year, 'string'),
        fuel_type: sanitize(editData.fuel_type, 'string'),
        transmission: sanitize(editData.transmission, 'string'),
        vehicle_colour: sanitize(editData.vehicle_colour, 'string'),
        engine_cc: sanitize(editData.engine_cc, 'number'),
        engine_number: sanitize(editData.engine_number, 'string'),
        chassis_number: sanitize(editData.chassis_number, 'string'),
        kms_driven: sanitize(editData.kms_driven, 'number'),
        owners: sanitize(editData.owners, 'number'),
        engine_condition: sanitize(editData.engine_condition, 'string') || 'PENDING',
        interior_condition: sanitize(editData.interior_condition, 'string') || 'PENDING',
        exterior_condition: sanitize(editData.exterior_condition, 'string') || 'PENDING',
        transmission_condition: sanitize(editData.transmission_condition, 'string') || 'PENDING',
        accident_history: editData.accident_history || false,
        flood_damage: editData.flood_damage || false,
        dents_scratches: editData.dents_scratches || false,
        insurance_status: sanitize(editData.insurance_status, 'string'),
        insurer_name: sanitize(editData.insurer_name, 'string'),
        policy_number: sanitize(editData.policy_number, 'string'),
        insurance_expiry: sanitize(editData.insurance_expiry, 'string'),
        policy_type: sanitize(editData.policy_type, 'string'),
        idv_value: sanitize(editData.idv_value, 'number'),
        repairs: repairs,
        total_repair_cost_min: repairTotals.min,
        total_repair_cost_max: repairTotals.max,
        rto_verification_status: sanitize(editData.rto_verification_status, 'string') || 'PENDING',
        hypothecation: sanitize(editData.hypothecation, 'string'),
        blacklist_status: editData.blacklist_status || false,
        is_financed: editData.is_financed || false,
        tax_valid_upto: sanitize(editData.tax_valid_upto, 'string'),
        fitness_upto: sanitize(editData.fitness_upto, 'string'),
        pucc_number: sanitize(editData.pucc_number, 'string'),
        pucc_valid_upto: sanitize(editData.pucc_valid_upto, 'string'),
        // Additional Vehicle & Registration fields
        car_number: sanitize(editData.car_number, 'string'),
        body_type: sanitize(editData.body_type, 'string'),
        vehicle_class: sanitize(editData.vehicle_class, 'string'),
        cylinders: sanitize(editData.cylinders, 'number'),
        seating_capacity: sanitize(editData.seating_capacity, 'number'),
        emission_norms: sanitize(editData.emission_norms, 'string'),
        gross_weight: sanitize(editData.gross_weight, 'string'),
        unladen_weight: sanitize(editData.unladen_weight, 'string'),
        wheelbase: sanitize(editData.wheelbase, 'string'),
        manufacturing_date: sanitize(editData.manufacturing_date, 'string'),
        registration_date: sanitize(editData.registration_date, 'string'),
        rc_expiry_date: sanitize(editData.rc_expiry_date, 'string'),
        registration_authority: sanitize(editData.registration_authority, 'string'),
        owner_name: sanitize(editData.owner_name, 'string'),
        overall_rating_locked: editData.overall_rating_locked || false,
        category_ratings: editData.category_ratings || {},
        overall_rating_history: editData.overall_rating_history || [],
        updated_at: new Date().toISOString()
      };
      
      // Remove undefined values (but keep empty strings and 0s)
      Object.keys(updatePayload).forEach(key => {
        if (updatePayload[key] === undefined) {
          delete updatePayload[key];
        }
      });
      
      // P0 data-loss guard: only overwrite the manual repairs list (and its
      // derived totals) when the curator actually edited repairs in THIS session.
      // Otherwise drop them from the payload so a stale/empty editData snapshot
      // can never wipe repairs that were saved from another tab/session.
      if (!isFieldManuallyEdited('repairs')) {
        delete updatePayload.repairs;
        delete updatePayload.total_repair_cost_min;
        delete updatePayload.total_repair_cost_max;
      }
      
      console.log('Saving inspection with payload:', Object.keys(updatePayload));
      await inspectionsApi.update(inspection.id, updatePayload);
      
      // Also save repair selection (auto-detected repairs visibility)
      // This ensures "Save All Changes" saves EVERYTHING including repair selections
      // ALWAYS include manual repairs — they were explicitly added by the user and must always appear
      const manualRepairIds = (editData.repairs || [])
        .filter(r => r.part_id || r.id)
        .map(r => r.part_id || r.id);
      
      const allSelectedRepairs = new Set([...selectedRepairs, ...manualRepairIds]);
      
      if (allSelectedRepairs.size > 0 || calculatedRepairCosts.length > 0) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/inspections/${inspection.id}/repair-report-selection`,
            {
              selected_repairs: Array.from(allSelectedRepairs),
              cost_overrides: repairCostOverrides
            }
          );
          console.log('Repair selection saved:', allSelectedRepairs.size, 'repairs (incl', manualRepairIds.length, 'manual)');
        } catch (repairErr) {
          console.error('Failed to save repair selection:', repairErr);
          // Don't fail the whole save, but warn
          toast.warning('Some repair selections may not have saved');
        }
      }
      
      toast.success('Changes saved successfully');
      
      // Update original data to reflect saved state
      setOriginalData(JSON.stringify({
        overall_rating: editData.overall_rating,
        recommended_to_buy: editData.recommended_to_buy,
        market_value_min: editData.market_value_min,
        market_value_max: editData.market_value_max,
        assessment_summary: editData.assessment_summary,
        repairs: repairs
      }));
      setHasUnsavedChanges(false);
      
      // Refresh inspection data — also refetch full inspection so parent has latest category_ratings etc.
      if (onRefresh) {
        onRefresh(inspection.id);
      }
      if (onInspectionUpdated) {
        try {
          const freshData = await inspectionsApi.getById(inspection.id);
          onInspectionUpdated(freshData.data);
        } catch (e) {
          // Non-critical: live progress refresh already ran
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      // Handle different error formats
      let errorMessage = 'Failed to save changes';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as array
          errorMessage = detail.map(e => e.msg || e.message || String(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  
  // Generate AI Report
  const generateAIReport = async () => {
    if (!inspection?.id) return;
    
    setGeneratingAI(true);
    try {
      const response = await inspectionsApi.generateAIReport(inspection.id, true);
      if (response.data.success) {
        toast.success('AI Report generated successfully');
        
        // Update local state with AI insights
        // IMPORTANT: Regenerating AI report UNLOCKS the overall_rating
        const ai = response.data.ai_insights;
        setEditData(prev => ({
          ...prev,
          overall_rating: ai.overall_rating || prev.overall_rating,
          overall_rating_locked: false, // UNLOCK - AI regeneration resets the lock
          recommended_to_buy: ai.recommended_to_buy ?? prev.recommended_to_buy,
          market_value_min: ai.market_value?.min || prev.market_value_min,
          market_value_max: ai.market_value?.max || prev.market_value_max,
          assessment_summary: typeof ai.assessment_summary === 'object' 
            ? ai.assessment_summary.overall || '' 
            : ai.assessment_summary || prev.assessment_summary,
          key_highlights: ai.key_highlights || prev.key_highlights,
          engine_condition: ai.condition_ratings?.engine || prev.engine_condition,
          interior_condition: ai.condition_ratings?.interior || prev.interior_condition,
          exterior_condition: ai.condition_ratings?.exterior || prev.exterior_condition,
          transmission_condition: ai.condition_ratings?.transmission || prev.transmission_condition
        }));
        
        onRefresh(inspection.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AI report');
    } finally {
      setGeneratingAI(false);
    }
  };
  
  // Fetch Recommended Purchase Price (RPP) from car portals
  const fetchRPP = async () => {
    if (!inspection?.id) return;
    
    // Check if we have vehicle details
    const hasVehicleDetails = inspection.vehicle_make && inspection.vehicle_model;
    if (!hasVehicleDetails) {
      toast.error('Vehicle make and model are required to fetch market prices');
      return;
    }
    
    setFetchingRPP(true);
    try {
      const response = await inspectionsApi.fetchRPP(inspection.id);
      if (response.data.success) {
        toast.success(`Market prices fetched from ${response.data.sources_count} sources!`);
        
        // Update local state with RPP values
        setEditData(prev => ({
          ...prev,
          market_value_min: response.data.recommended_min || prev.market_value_min,
          market_value_max: response.data.recommended_max || prev.market_value_max,
        }));
        
        // Refresh inspection data to show full breakdown
        onRefresh(inspection.id);
      } else {
        toast.warning('Could not fetch market prices. Using estimation model.');
        onRefresh(inspection.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch market prices');
    } finally {
      setFetchingRPP(false);
    }
  };
  
  // Extract KM from dashboard photo
  const extractKMFromDashboardPhoto = async (imageUrl) => {
    if (!inspection?.id || !imageUrl) return;
    
    setExtractingKM(true);
    setKmExtractionResult(null);
    
    try {
      const response = await inspectionsApi.extractKMFromPhoto(inspection.id, imageUrl);
      
      if (response.data.success) {
        const needsReview = response.data.needs_review;
        const autoApplied = response.data.auto_applied;
        
        setKmExtractionResult({
          success: true,
          km_reading: response.data.km_reading,
          confidence: response.data.confidence,
          notes: response.data.notes,
          display_type: response.data.display_type,
          needs_review: needsReview,
          validation_flags: response.data.validation_flags || [],
          all_readings_seen: response.data.all_readings_seen || [],
          auto_applied: autoApplied,
        });
        
        // Only auto-update editData if the reading was auto-applied AND user hasn't manually entered KMS
        if (autoApplied) {
          setEditData(prev => {
            // Don't overwrite if user has manually edited kms_driven
            if (isFieldManuallyEdited('kms_driven')) {
              console.log('[KM_EXTRACTION] Skipping auto-apply: field was manually edited');
              return prev;
            }
            // Don't overwrite if user already has a manual KM reading
            if (prev.kms_driven && prev.kms_driven > 0 && prev.kms_driven !== response.data.km_reading) {
              console.log('[KM_EXTRACTION] Skipping auto-apply: user has manual entry', prev.kms_driven);
              return prev;
            }
            return { ...prev, kms_driven: response.data.km_reading };
          });
        }
        
        // Refresh inspection data to sync
        if (onInspectionUpdated && response.data.inspection) {
          onInspectionUpdated(response.data.inspection);
        }
        
        if (needsReview) {
          const flags = (response.data.validation_flags || []).join('; ');
          toast.warning(`KM reading ${response.data.km_reading.toLocaleString()} km needs manual review. ${flags}`, { duration: 8000 });
        } else {
          toast.success(`KM reading extracted: ${response.data.km_reading.toLocaleString()} km (${Math.round(response.data.confidence * 100)}% confidence)`);
        }
      } else {
        setKmExtractionResult({
          success: false,
          error: response.data.error,
          suggestions: response.data.suggestions
        });
        toast.warning(`Could not extract KM: ${response.data.error}`);
      }
    } catch (error) {
      console.error('KM extraction failed:', error);
      setKmExtractionResult({
        success: false,
        error: error.response?.data?.detail || error.message
      });
      toast.error('Failed to extract KM from photo');
    } finally {
      setExtractingKM(false);
    }
  };
  
  // Watch for dashboard odometer photo in live progress data
  useEffect(() => {
    if (!liveProgressData || !inspection?.id || kmExtractionProcessed) return;
    
    // Only extract if KM hasn't been extracted yet for this inspection
    if (inspection.ai_extracted_kms) return;
    
    // Skip auto-extraction if inspection already has a saved KMS value (manual or previous AI)
    if (inspection.kms_driven && inspection.kms_driven > 0) return;
    
    // Known dashboard odometer photo question ID
    const DASHBOARD_ODOMETER_QUESTION_ID = '3ca03df7-458b-4da4-b0d9-f938614c4a12';
    
    // Look for "Dashboard Odometer Photo" question answer in progress data
    const categories = liveProgressData.categories || [];
    
    // Helper to check if answer is a valid photo URL
    const isPhotoAnswer = (answer) => {
      return answer && typeof answer === 'string' && 
        (answer.includes('firebase') || answer.includes('http') || 
         answer.startsWith('media_ref:') || answer.startsWith('data:image'));
    };
    
    // Helper to extract answer string from various formats
    const extractAnswerString = (answerData) => {
      if (!answerData) return null;
      if (typeof answerData === 'string') return answerData;
      if (typeof answerData === 'object' && answerData.answer) return answerData.answer;
      return null;
    };
    
    // Strategy 1: Check all categories for matching questions
    for (const category of categories) {
      const questions = category.questions || {};
      const questionsArr = Array.isArray(questions) ? questions : Object.entries(questions);
      
      for (const item of questionsArr) {
        const [questionId, questionData] = Array.isArray(item) ? item : [item.question_id, item];
        
        // Check if this is the dashboard odometer photo question
        const questionText = (questionData.question_text || '').toLowerCase();
        
        // Match by question ID OR by text pattern
        const isOdometerQuestion = 
          questionId === DASHBOARD_ODOMETER_QUESTION_ID ||
          (questionText.includes('dashboard') && questionText.includes('odometer')) ||
          questionText.includes('odometer photo') ||
          questionText.includes('km reading');
        
        if (isOdometerQuestion) {
          const answer = extractAnswerString(questionData.answer);
          
          if (isPhotoAnswer(answer)) {
            console.log('[KM_EXTRACTION] Found dashboard odometer photo, starting extraction...');
            console.log('[KM_EXTRACTION] Question ID:', questionId);
            console.log('[KM_EXTRACTION] Answer format:', answer.startsWith('media_ref:') ? 'media_ref (base64)' : 'URL');
            setKmExtractionProcessed(true);
            extractKMFromDashboardPhoto(answer);
            return; // Exit after triggering extraction
          }
        }
      }
    }
    
    // Strategy 2: Check inspection_answers directly with known question ID
    if (inspection?.inspection_answers) {
      const directAnswer = inspection.inspection_answers[DASHBOARD_ODOMETER_QUESTION_ID];
      const answer = extractAnswerString(directAnswer);
      
      if (isPhotoAnswer(answer)) {
        console.log('[KM_EXTRACTION] Found answer via direct inspection_answers lookup');
        console.log('[KM_EXTRACTION] Answer format:', answer.startsWith('media_ref:') ? 'media_ref (base64)' : 'URL');
        setKmExtractionProcessed(true);
        extractKMFromDashboardPhoto(answer);
      }
    }
  }, [liveProgressData, inspection?.id, inspection?.inspection_answers, kmExtractionProcessed]);
  
  // Reset extraction state when inspection changes
  useEffect(() => {
    setKmExtractionProcessed(false);
    setKmExtractionResult(null);
    setExtractingKM(false); // never carry a stale "Extracting…" state to the next vehicle
  }, [inspection?.id]);
  
  // Generate Share URL
  const generateShareUrl = async () => {
    if (!inspection?.id) return;
    
    setGeneratingShareUrl(true);
    try {
      const response = await inspectionsApi.getShortUrl(inspection.id);
      if (response.data.url) {
        setShareUrl(response.data.url);
        toast.success('Share link generated!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate share link');
    } finally {
      setGeneratingShareUrl(false);
    }
  };
  
  // Copy share URL to clipboard
  const copyShareUrl = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };
  
  // Fetch publish history and portal audit
  const fetchPublishHistory = async () => {
    if (!inspection?.id) return;
    setLoadingPublishHistory(true);
    try {
      const response = await inspectionsApi.getPublishHistory(inspection.id);
      setPublishHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch publish history:', error);
    }
    // Also fetch portal audit if customer_id exists
    try {
      if (inspection?.customer_id) {
        const auditRes = await inspectionsApi.getPortalAudit(inspection.customer_id);
        setPortalAudit(auditRes.data || { events: [], active_otp: null });
      }
    } catch (error) {
      console.error('Failed to fetch portal audit:', error);
    }
    setLoadingPublishHistory(false);
  };
  
  // Publish report to customer
  const handlePublishReport = async () => {
    if (!inspection?.id) return;
    
    // Check if customer phone exists (field is customer_mobile in DB)
    if (!inspection?.customer_mobile && !inspection?.customer_phone) {
      toast.error('Customer phone number is required to publish report');
      return;
    }
    
    setPublishingReport(true);
    try {
      const response = await inspectionsApi.publishReport(inspection.id, {
        custom_message: publishNotes,
        send_whatsapp: true,
        send_sms: true
      });
      
      const result = response.data;
      const notifications = [];
      if (result.whatsapp_sent) notifications.push('WhatsApp');
      if (result.sms_sent) notifications.push('SMS');
      
      if (result.success) {
        toast.success(
          `Report published successfully! ${notifications.length > 0 ? `Sent via ${notifications.join(' & ')}` : ''}\n` +
          `Report URL: ${result.report_url}`,
          { duration: 5000 }
        );
        
        // Copy report URL to clipboard
        if (result.report_url) {
          navigator.clipboard.writeText(result.report_url).then(() => {
            toast.info('Report URL copied to clipboard!');
          }).catch(() => {});
        }
      } else {
        toast.warning(`Report published but notifications may have failed: ${result.errors?.join(', ')}`);
      }
      
      setPublishNotes('');
      setShowPublishModal(false);
      
      // Refresh publish history
      fetchPublishHistory();
      
      // Refresh inspection data
      onRefresh(inspection.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish report');
    } finally {
      setPublishingReport(false);
    }
  };
  
  // Preview report in new tab - Opens authenticated preview (CRM internal only)
  const handlePreviewReport = () => {
    if (!inspection?.id) return;
    
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      toast.warning('You have unsaved changes. Please save all changes first before previewing the report.');
      return;
    }
    
    // Use the new authenticated preview route
    const baseUrl = window.location.origin;
    const reportUrl = `${baseUrl}/preview-report/${inspection.id}`;
    window.open(reportUrl, '_blank');
  };
  
  // Edit a single answer with predefined options and optional sub-answers
  const updateAnswer = async (questionId, newAnswer, options = [], sub1, sub2) => {
    if (!inspection?.id || !questionId) return;
    
    // If options provided, validate that newAnswer is in options
    if (options.length > 0 && newAnswer && !options.includes(newAnswer)) {
      toast.error('Please select a valid option');
      return;
    }
    
    setSavingAnswer(true);
    try {
      const payload = { answer: newAnswer || '' };
      if (sub1 !== undefined) payload.sub_answer_1 = sub1;
      if (sub2 !== undefined) payload.sub_answer_2 = sub2;
      
      await inspectionsApi.editAnswer(inspection.id, questionId, payload);
      toast.success('Answer updated successfully');
      setEditingAnswer(null);
      onRefresh(inspection.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update answer');
    } finally {
      setSavingAnswer(false);
    }
  };
  
  // Manual refresh - also fetches Vaahan data and validates missing data
  const handleManualRefresh = async () => {
    if (!inspection?.id) return;
    setRefreshing(true);
    try {
      // Fetch Vaahan data first to update vehicle details
      try {
        const vaahanResponse = await inspectionsApi.fetchVaahanData(inspection.id);
        if (vaahanResponse.data?.success) {
          // Update editData with fetched vehicle info
          const vaahanData = vaahanResponse.data.vaahan_data || {};
          const updatedInspection = vaahanResponse.data.inspection || {};
          
          setEditData(prev => {
            // Normalize fuel_type from Vaahan (often UPPERCASE) to match dropdown options
            const rawFuel = updatedInspection.fuel_type || prev.fuel_type || '';
            let normalizedFuel = prev.fuel_type;
            if (rawFuel) {
              const upperFuel = rawFuel.toUpperCase();
              if (upperFuel.includes('PETROL')) normalizedFuel = 'Petrol';
              else if (upperFuel.includes('DIESEL')) normalizedFuel = 'Diesel';
              else if (upperFuel.includes('CNG')) normalizedFuel = 'CNG';
              else if (upperFuel.includes('LPG')) normalizedFuel = 'LPG';
              else if (upperFuel.includes('ELECTRIC') || upperFuel.includes('EV') || upperFuel.includes('BEV')) normalizedFuel = 'Electric';
              else if (upperFuel.includes('HYBRID')) normalizedFuel = 'Hybrid';
              else normalizedFuel = rawFuel.charAt(0).toUpperCase() + rawFuel.slice(1).toLowerCase();
            }
            
            return {
              ...prev,
              vehicle_make: updatedInspection.vehicle_make || prev.vehicle_make,
              vehicle_model: updatedInspection.vehicle_model || prev.vehicle_model,
              vehicle_year: updatedInspection.vehicle_year || prev.vehicle_year,
              vehicle_colour: updatedInspection.vehicle_colour || prev.vehicle_colour,
              fuel_type: normalizedFuel,
              owners: updatedInspection.owners || prev.owners,
              insurer_name: updatedInspection.insurer_name || prev.insurer_name,
              insurance_expiry: updatedInspection.insurance_expiry || prev.insurance_expiry,
              insurance_status: updatedInspection.insurance_status || prev.insurance_status,
              policy_number: updatedInspection.policy_number || prev.policy_number,
              hypothecation: updatedInspection.hypothecation || prev.hypothecation,
              blacklist_status: updatedInspection.blacklist_status || prev.blacklist_status,
              rto_verification_status: updatedInspection.rto_verification_status || prev.rto_verification_status,
            };
          });
          
          // Update the inspection object in parent component
          if (onInspectionUpdated) {
            onInspectionUpdated(updatedInspection);
          }
          
          toast.success('Vehicle data updated from Vaahan API');
        } else {
          // Vaahan API failed but we should still try to refresh the inspection data
          // to get any market_price_research that might have been generated by AI report
          try {
            const inspectionResponse = await inspectionsApi.getById(inspection.id);
            if (inspectionResponse.data && onInspectionUpdated) {
              onInspectionUpdated(inspectionResponse.data);
            }
          } catch (e) {
            console.log('Could not refresh inspection data:', e.message);
          }
        }
      } catch (vaahanError) {
        console.log('Vaahan fetch skipped or failed:', vaahanError?.response?.data?.error || vaahanError.message);
        // Even if Vaahan fails, still try to refresh the inspection data
        try {
          const inspectionResponse = await inspectionsApi.getById(inspection.id);
          if (inspectionResponse.data && onInspectionUpdated) {
            onInspectionUpdated(inspectionResponse.data);
          }
        } catch (e) {
          console.log('Could not refresh inspection data:', e.message);
        }
      }
      
      // Also refresh live progress data
      await onRefresh(inspection.id);
      
      // RECALCULATE OVERALL RATING from ALL displayed category ratings
      // This ensures the rating is calculated from all 11 categories shown in QA tab
      setTimeout(() => {
        setEditData(prev => {
          // Start with saved category_ratings
          const catRatings = { ...prev.category_ratings };
          
          // Add auto-calculated ratings for categories that don't have saved values
          const categories = liveProgressData?.categories || [];
          categories.forEach(cat => {
            const categoryKey = cat.category_name?.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
            if (!(categoryKey in catRatings)) {
              // Calculate auto-rating from answered questions
              const questions = cat.questions || [];
              const answered = questions.filter(q => q.is_answered);
              if (answered.length > 0) {
                const goodCount = answered.filter(q => {
                  const a = (typeof q.answer === 'string' ? q.answer : '').toLowerCase();
                  return !a.includes('poor') && !a.includes('bad') && !a.includes('damaged') && !a.includes('broken') && !a.includes('not working') && !a.includes('faulty') && !a.includes('average') && !a.includes('fair') && !a.includes('needs attention') && !a.includes('worn') && !a.includes('minor');
                }).length;
                catRatings[categoryKey] = Math.round((goodCount / answered.length) * 10);
              }
            }
          });
          
          const allRatings = Object.values(catRatings).filter(r => r !== undefined && r !== null && r > 0);
          
          if (allRatings.length > 0) {
            const sum = allRatings.reduce((a, b) => a + b, 0);
            const avgRating = Math.round((sum / allRatings.length) * 10) / 10;
            console.log(`[Refresh] Recalculated overall_rating: ${avgRating} from ${allRatings.length} categories (sum: ${sum})`);
            
            // Also recalculate conditions
            const conditionUpdates = calculateConditionsFromRatings(catRatings, categories);
            
            // Reset ALL manual edit flags since user explicitly clicked Refresh button
            // This allows the system to recalculate everything fresh
            manuallyEditedFieldsRef.current = new Set();
            console.log('[Refresh] Cleared all manual edit flags');
            
            return {
              ...prev,
              category_ratings: catRatings,
              overall_rating: avgRating,
              ...conditionUpdates
            };
          }
          return prev;
        });
      }, 100);
      
      // After refresh, validate and show missing data
      setTimeout(() => {
        const missing = validateMissingData();
        setMissingDataItems(missing);
        if (missing.length > 0) {
          setShowMissingDataAlert(true);
          
          // Auto-fetch market prices if Market Value is missing and vehicle details are available
          const hasMarketValueMissing = missing.some(m => m.field.includes('Market Value'));
          const hasVehicleDetails = inspection?.vehicle_make && inspection?.vehicle_model;
          if (hasMarketValueMissing && hasVehicleDetails && !fetchingRPP) {
            fetchRPP();
          }
          
          toast.info(`Refresh complete. ${missing.length} item(s) need attention.`);
        } else {
          toast.success('Data refreshed - All required fields are complete!');
        }
      }, 500);

      // KM Tamper Check: compare service history KM with current odometer
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const shRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/inspections/${inspection.id}/service-history`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const shSummary = shRes.data?.summary;
        const shRecords = shRes.data?.records || [];

        if (shSummary?.odometer_tampered) {
          toast.error(
            `ODOMETER TAMPERING: Current reading (${Number(shSummary.current_odometer).toLocaleString('en-IN')} km) is LOWER than last service record (${Number(shSummary.last_service_km).toLocaleString('en-IN')} km). KM may have been reversed!`,
            { duration: 15000 }
          );
        }

        // Auto-update accident_history and flood_damage from service history records
        const hasAccident = shRecords.some(r => r.is_accident_related);
        const hasFlood = shRecords.some(r => r.is_flood_damage);
        if (hasAccident || hasFlood) {
          setEditData(prev => {
            const updates = {};
            if (hasAccident && !prev.accident_history) {
              updates.accident_history = true;
              toast.warning('Accidental repairs found in service history — "Accident History" set to YES', { duration: 6000 });
            }
            if (hasFlood && !prev.flood_damage) {
              updates.flood_damage = true;
              toast.warning('Flood damage found in service history — "Flood Damage" set to YES', { duration: 6000 });
            }
            return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
          });
        }
      } catch (e) {
        // Service history check is non-blocking
      }
    } finally {
      setTimeout(() => setRefreshing(false), 500);
    }
  };
  
  // Get categories to display based on filter
  const categoriesToDisplay = selectedCategoryId 
    ? liveProgressData?.categories?.filter(c => c.category_id === selectedCategoryId)
    : liveProgressData?.categories;
  
  // Get completion stats
  const stats = liveProgressData?.overall_stats || {};
  const aiReport = liveProgressData?.ai_report || {};
  const isInspCompleted = inspection?.inspection_status === 'INSPECTION_COMPLETED' || inspection?.status === 'COMPLETED';
  
  // Guard: Don't render if no inspection data
  if (!inspection) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-[400px]">
          <div className="flex items-center justify-center p-8">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading inspection...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        // If lightbox is showing, close only the lightbox, not the Dialog
        if (showQAImageModal) {
          setShowQAImageModal(false);
          setQaImageData(null);
          return;
        }
        onClose();
        // Cleanup lightbox state when dialog closes
        setShowQAImageModal(false);
        setQaImageData(null);
      }
    }}>
      <DialogContent className="max-w-[98vw] w-[98vw] max-h-[96vh] h-[96vh] overflow-hidden flex flex-col p-0 rounded-xl">
        {/* Compact Header */}
        <DialogHeader className="px-5 py-3 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white flex-shrink-0">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-white/20 flex items-center justify-center">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-base font-semibold flex items-center gap-2">
                  Inspection Editor
                  {stats.completion_percentage === 100 && (
                    <span className="text-xs bg-green-400 text-green-900 px-2 py-0.5 rounded-full">Complete</span>
                  )}
                </p>
                <p className="text-xs text-blue-100 font-normal">
                  {inspection?.customer_name} • {inspection?.car_number}
                  {liveProgressData?.mechanic_name && (
                    <span className="ml-2 px-2 py-0.5 bg-white/15 rounded text-blue-100">
                      Mechanic: {liveProgressData.mechanic_name}
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/*
                Media Quality Toggle (Apr 2026) — lets ops pick the upload tier
                (Low / Medium / High) for FUTURE photo uploads on this inspection.
                Locked once the inspection enters a terminal state.
                Backend: PATCH /api/inspections/{id}/media-quality
              */}
              {inspection?.id && <MediaQualityToggle inspectionId={inspection.id} />}

              {/*
                Bulk Media Download (Apr 2026) — one-click ZIP of every photo
                + video for this inspection, folder-named after the car number.
                Files are fetched directly from the CDN by the browser
                (zero Railway egress) and zipped client-side.
                Backend: GET /api/inspections/{id}/media-bundle (manifest only)
              */}
              {inspection?.id && <BulkMediaDownloadButton inspectionId={inspection.id} />}

              {/* Refresh Controls */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleManualRefresh}
                  disabled={refreshing}
                  className="h-8 px-2.5 bg-white/10 hover:bg-white/20 text-white text-xs"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-2 py-1">
                  <span className="text-xs text-blue-100">Auto</span>
                  <Switch
                    checked={autoRefresh}
                    onCheckedChange={setAutoRefresh}
                    className="data-[state=checked]:bg-green-500 scale-75"
                    data-testid="live-progress-auto-refresh-switch"
                  />
                  {autoRefresh && (
                    <span className="text-xs text-green-300 animate-pulse" data-testid="live-progress-poll-interval">{pollMs / 1000}s</span>
                  )}
                </div>
              </div>
              
              {/* Progress indicator */}
              <div className="text-right pl-2 border-l border-white/20">
                <p className="text-2xl font-bold leading-tight">{stats.completion_percentage || 0}%</p>
                <p className="text-[10px] text-blue-200">{stats.answered_questions || 0}/{stats.total_questions || 0} questions</p>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {/* Sidebar + Content Layout */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Sidebar Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-1 min-h-0" orientation="vertical">
            <div className="w-48 flex-shrink-0 bg-gray-50 border-r flex flex-col">
              <TabsList className="flex flex-col bg-transparent h-auto p-2 gap-1">
                <TabsTrigger value="ai-analysis" className="w-full justify-start data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg h-9 px-3 text-sm">
                  <Zap className="h-4 w-4 mr-2 flex-shrink-0" />
                  AI Analysis
                </TabsTrigger>
                <TabsTrigger value="vehicle" className="w-full justify-start data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg h-9 px-3 text-sm">
                  <Car className="h-4 w-4 mr-2 flex-shrink-0" />
                  Vehicle & RTO
                </TabsTrigger>
                <TabsTrigger value="obd" className="w-full justify-start data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg h-9 px-3 text-sm">
                  <Gauge className="h-4 w-4 mr-2 flex-shrink-0" />
                  OBD-2
                  {liveProgressData?.obd_scan?.completed && (
                    <span className="ml-auto h-2 w-2 rounded-full bg-green-500"></span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="repairs" className="w-full justify-start data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg h-9 px-3 text-sm">
                  <Wrench className={`h-4 w-4 mr-2 flex-shrink-0 ${calculatedRepairCosts.length > 0 && selectedRepairs.size === 0 ? 'text-gray-300' : ''}`} />
                  <span className={calculatedRepairCosts.length > 0 && selectedRepairs.size === 0 ? 'text-gray-400' : ''}>Repairs</span>
                  {calculatedRepairCosts.length > 0 && (
                    <span className={`ml-auto px-1.5 py-0.5 text-[10px] rounded-full ${selectedRepairs.size > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`}>{selectedRepairs.size > 0 ? selectedRepairs.size : 0}/{calculatedRepairCosts.length}</span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="inspection" className="w-full justify-start data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg h-9 px-3 text-sm">
                  <ClipboardList className="h-4 w-4 mr-2 flex-shrink-0" />
                  Q&A Details
                </TabsTrigger>
                <TabsTrigger value="service-history" className="w-full justify-start data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg h-9 px-3 text-sm" data-testid="service-history-tab">
                  <History className="h-4 w-4 mr-2 flex-shrink-0" />
                  Service History
                </TabsTrigger>
                <TabsTrigger value="report" className="w-full justify-start data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg h-9 px-3 text-sm" data-testid="report-tab">
                  <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                  Report
                  {inspection?.publish_count > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded-full">{inspection.publish_count}x</span>
                  )}
                </TabsTrigger>
              </TabsList>
              
              {/* Missing Data Alert - Sidebar */}
              {showMissingDataAlert && missingDataItems.length > 0 && (
                <div className="mx-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                      <span className="text-xs font-medium text-amber-800">Missing ({missingDataItems.length})</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowMissingDataAlert(false)}
                      className="h-5 w-5 p-0 hover:bg-amber-100"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="space-y-0.5 max-h-40 overflow-y-auto">
                    {missingDataItems.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 text-[11px]">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          item.priority === 'high' ? 'bg-red-500' : 
                          item.priority === 'medium' ? 'bg-amber-500' : 'bg-gray-400'
                        }`}></span>
                        <span className="text-amber-700 truncate">{item.field}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          
            {/* Content Area */}
            <div className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden" style={{ width: 0 }}>
              <div className="p-5 pb-6 w-full max-w-full">
                {/* AI Analysis Tab (formerly Overview) */}
                <AIAnalysisTab
                  editData={editData} updateField={updateField} canEdit={canEdit}
                  inspection={inspection} aiReport={aiReport}
                  generatingAI={generatingAI} generateAIReport={generateAIReport}
                  fetchingRPP={fetchingRPP} fetchRPP={fetchRPP}
                  marketPriceResearch={marketPriceResearch}
                  onShowRatingBreakdown={() => setShowRatingBreakdownModal(true)}
                  liveProgressData={liveProgressData}
                />
            {/* Vehicle Tab - Comprehensive Vaahan API Data */}
                <VehicleTab
                  editData={editData} updateField={updateField} canEdit={canEdit}
                  inspection={inspection} liveProgressData={liveProgressData}
                  onInspectionUpdated={onInspectionUpdated}
                  kmExtractionResult={kmExtractionResult}
                  extractingKM={extractingKM}
                  extractKMFromDashboardPhoto={extractKMFromDashboardPhoto}
                  editingKmReading={editingKmReading}
                  setEditingKmReading={setEditingKmReading}
                  manualKmValue={manualKmValue}
                  setManualKmValue={setManualKmValue}
                  loadingQAImage={loadingQAImage}
                />
            {/* Q&A Details Tab (formerly Inspection) - Last tab with editable answers */}
                <QATab
                  editData={editData} updateField={updateField} canEdit={canEdit}
                  inspection={inspection} liveProgressData={liveProgressData}
                  selectedCategoryId={selectedCategoryId}
                  setSelectedCategoryId={setSelectedCategoryId}
                  categoriesToDisplay={categoriesToDisplay}
                  editingAnswer={editingAnswer}
                  setEditingAnswer={setEditingAnswer}
                  savingAnswer={savingAnswer}
                  updateAnswer={updateAnswer}
                  viewQAImage={viewQAImage}
                  isInspCompleted={isInspCompleted}
                  onRefresh={onRefresh}
                />
            {/* OBD-2 Diagnostics Tab - NEW */}
                <OBDTab
                  canEdit={canEdit} inspection={inspection}
                  liveProgressData={liveProgressData}
                  dtcCodes={dtcCodes}
                  selectedDtcCodes={selectedDtcCodes}
                  toggleDtcSelection={toggleDtcSelection}
                  savingDtcSelection={savingDtcSelection}
                  saveDtcSelection={saveDtcSelection}
                  requestingRescan={requestingRescan}
                  requestObdRescan={requestObdRescan}
                  onRefresh={() => onRefresh && onRefresh(inspection.id)}
                />
                <RepairsTab
                  editData={editData} canEdit={canEdit} inspection={inspection}
                  saving={saving} saveChanges={saveChanges}
                  calculatedRepairCosts={calculatedRepairCosts}
                  repairsByCategory={repairsByCategory}
                  selectedRepairs={selectedRepairs}
                  toggleRepairSelection={toggleRepairSelection}
                  selectAllRepairs={selectAllRepairs}
                  updateRepairCostOverride={updateRepairCostOverride}
                  getEffectiveRepairCost={getEffectiveRepairCost}
                  editingRepairCost={editingRepairCost}
                  setEditingRepairCost={setEditingRepairCost}
                  savingRepairSelection={savingRepairSelection}
                  saveRepairSelection={saveRepairSelection}
                  updatingMasterCost={updatingMasterCost}
                  updateMasterRepairCost={updateMasterRepairCost}
                  getRepairKey={getRepairKey}
                  expandedCategories={expandedCategories}
                  toggleCategoryExpansion={toggleCategoryExpansion}
                  addRepair={addRepair} removeRepair={removeRepair}
                  updateRepair={updateRepair} saveRepairToMaster={saveRepairToMaster}
                  repairParts={repairParts} partCategories={partCategories}
                  calculateRepairTotals={calculateRepairTotals}
                  setSaveToMasterRepair={setSaveToMasterRepair}
                  updateField={updateField}
                />
            {/* Report Tab */}
                <ReportTab
                  inspection={inspection} publishHistory={publishHistory}
                  publishingReport={publishingReport}
                  loadingPublishHistory={loadingPublishHistory}
                  showPublishModal={showPublishModal}
                  setShowPublishModal={setShowPublishModal}
                  publishNotes={publishNotes}
                  setPublishNotes={setPublishNotes}
                  handlePublishReport={handlePublishReport}
                  handlePreviewReport={handlePreviewReport}
                  fetchPublishHistory={fetchPublishHistory}
                  portalAudit={portalAudit}
                  hasUnsavedChanges={hasUnsavedChanges}
                />

                {/* Service History Tab */}
                <ServiceHistoryTab
                  inspection={inspection}
                  currentOdometer={editData?.kms_driven || inspection?.kms_driven}
                  onUpdateEditData={(field, value) => setEditData(prev => ({ ...prev, [field]: value }))}
                />
              </div>
            </div>
          </Tabs>
        </div>
        
        {/* Footer with Save Button */}
        {canEdit && (
          <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className="text-xs text-gray-500">
                {liveProgressData?.updated_at && (
                  <span>Last updated: {new Date(liveProgressData.updated_at).toLocaleString()}</span>
                )}
              </div>
              {/* Unsaved Changes Indicator */}
              {hasUnsavedChanges && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium animate-pulse">
                  <AlertCircle className="h-3 w-3" />
                  Unsaved Changes
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={saveChanges}
                disabled={saving}
                className={`${hasUnsavedChanges ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600' : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'}`}
                data-testid="save-all-btn"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" />Save All Changes{hasUnsavedChanges && ' *'}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
      {/* Save to Master Modal */}
      {saveToMasterRepair && (
        <SaveToMasterModal
          isOpen={!!saveToMasterRepair}
          onClose={() => setSaveToMasterRepair(null)}
          repair={saveToMasterRepair}
          repairParts={repairParts}
          vehicleMake={editData?.car_make || ''}
          carType={editData?.car_type || 'sedan'}
          onSaveComplete={() => setSaveToMasterRepair(null)}
          countryId={inspection?.country_id}
        />
      )}
    </Dialog>

    {/* Q&A Media Lightbox — rendered via Portal to escape Dialog's z-index */}
    {showQAImageModal && createPortal(
      <div 
        style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.92)' }}
        onClick={(e) => { if (e.target === e.currentTarget) { setShowQAImageModal(false); setQaImageData(null); } }}
        data-testid="qa-media-modal"
      >
        {/* Top bar with close button — always visible, outside image area */}
        <div 
          style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100001, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)', pointerEvents: 'auto' }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* Title on the left */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {qaImageTitle && <p style={{ color: 'white', fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100vw - 100px)' }}>{qaImageTitle}</p>}
          </div>
          {/* Close button — large, clearly separated from image */}
          <button
            onClick={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); setShowQAImageModal(false); setQaImageData(null); }}
            onPointerDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
            style={{ 
              flexShrink: 0, marginLeft: 12, width: 44, height: 44, 
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.5)',
              borderRadius: 12, color: '#fff', cursor: 'pointer',
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              transition: 'all 0.15s ease',
              pointerEvents: 'auto', position: 'relative', zIndex: 100002
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; e.currentTarget.style.borderColor = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; }}
            data-testid="qa-media-close-btn"
          >
            <X className="h-5 w-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Content area */}
        <div style={{ maxWidth: '90vw', maxHeight: 'calc(100vh - 80px)', marginTop: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }} onClick={() => { setShowQAImageModal(false); setQaImageData(null); }}>
          
          {loadingQAImage ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 400, height: 300, background: '#1a1a1a', borderRadius: 12 }}>
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          ) : qaImageData ? (
            (isVideoUrl(qaImageData) || qaMediaType === 'video') ? (
              <div 
                onClick={(e) => e.stopPropagation()} 
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, pointerEvents: 'auto', cursor: 'default' }}
              >
                {/* Video player - try inline playback first */}
                <video 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/media/video-proxy?url=${encodeURIComponent(qaImageData)}`}
                  controls 
                  playsInline
                  preload="auto"
                  style={{ maxWidth: '85vw', maxHeight: '60vh', borderRadius: 8, background: '#000', pointerEvents: 'auto', cursor: 'default' }}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onError={(e) => {
                    console.warn('Video playback not supported in this browser, download to play locally');
                    e.target.style.display = 'none';
                    const notice = document.getElementById('video-codec-notice');
                    if (notice) notice.style.display = 'flex';
                  }}
                />
                {/* Codec not supported notice (hidden by default, shown on error) */}
                <div id="video-codec-notice" style={{ display: 'none', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 24, background: '#111827', borderRadius: 12, color: '#9ca3af', width: 420, border: '1px solid #1f2937' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5"><path d="M15.75 10.5l4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9A2.25 2.25 0 0 0 13.5 5.25h-9A2.25 2.25 0 0 0 2.25 7.5v9A2.25 2.25 0 0 0 4.5 18.75z"/></svg>
                  <p style={{ fontSize: 14, fontWeight: 500, color: '#e5e7eb' }}>Video format not supported in browser</p>
                  <p style={{ fontSize: 12, color: '#6b7280', textAlign: 'center' }}>Use the buttons below to download or open in a new tab</p>
                </div>
                {/* Action buttons - ALWAYS visible */}
                <div 
                  style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', pointerEvents: 'auto' }}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/media/video-download?url=${encodeURIComponent(qaImageData)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', 
                      background: '#2563eb', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      textDecoration: 'none', cursor: 'pointer', pointerEvents: 'auto', position: 'relative', zIndex: 100003
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    data-testid="video-download-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download Video
                  </a>
                  <a 
                    href={qaImageData}
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ 
                      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                      background: '#1e293b', color: '#94a3b8', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      textDecoration: 'none', border: '1px solid #334155', cursor: 'pointer', pointerEvents: 'auto', position: 'relative', zIndex: 100003
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    data-testid="video-open-tab-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    Open in New Tab
                  </a>
                </div>
              </div>
            ) : (
              <img 
                src={qaImageData} 
                alt={qaImageTitle || 'Media'} 
                style={{ maxWidth: '85vw', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8 }} 
              />
            )
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 400, height: 300, background: '#1a1a1a', borderRadius: 12, color: '#9ca3af' }}>
              Failed to load media
            </div>
          )}
        </div>
      </div>,
      document.body
    )}
    
    {/* Rating Breakdown Modal */}
    <RatingBreakdownModal
      isOpen={showRatingBreakdownModal}
      onClose={() => setShowRatingBreakdownModal(false)}
      editData={editData}
      liveProgressData={liveProgressData}
      inspection={inspection}
      onRecalculate={(newRating) => {
        // Apply the recalculated rating and unlock it
        setEditData(prev => ({
          ...prev,
          overall_rating: newRating,
          overall_rating_locked: false,
          overall_rating_history: [
            ...(prev.overall_rating_history || []),
            {
              timestamp: new Date().toISOString(),
              type: 'manual_recalculation',
              old_value: prev.overall_rating,
              new_value: newRating,
              changed_by: user?.name || user?.email || 'User',
              reason: 'Manual recalculation triggered from Rating Breakdown modal'
            }
          ]
        }));
        toast.success(`Overall rating updated to ${newRating}`);
      }}
    />
    </>
  );
}
import { activeCurrencySymbol } from '@/utils/currency';
